import React from "react";
import { useLocation } from "react-router-dom";
import { AiLoadError, useAiRequestGuard, withAiAgentScope } from "./aiAgentRequests";
import useAiAgentDialog from "./useAiAgentDialog";
import { normalizeSenderList, senderLabel } from "./aiAgentSenders";
import { initialAiAgentSection } from "./aiAgentNavigation";
import AiQualificationWorkspace from "./AiQualificationWorkspace";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Bot,
  Eye,
  Image as ImageIcon,
  ListChecks,
  Info,
  MessageSquare,
  Clock,
  MapPin,
  Pencil,
  Plus,
  RefreshCcw,
  Save,
  Settings2,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  activateDelayedResponsePolicy,
  activateControlledAnswerCard,
  activateLeadQualificationConfirmationCheckpoint,
  createAiBusinessProofLocation,
  createAiLeadFlowField,
  createAiLeadMediaTriggerRule,
  createControlledAnswerCard,
  createDelayedResponsePolicy,
  createLeadQualificationConfirmationCheckpoint,
  createLeadQualificationConfirmationCheckpointV2,
  createLeadQualificationReminderStep,
  deactivateDelayedResponsePolicy,
  deactivateAiBusinessProofLocation,
  deactivateControlledAnswerCard,
  deactivateLeadQualificationConfirmationCheckpoint,
  deactivateLeadQualificationReminderStep,
  convertLeadQualificationConfirmationCheckpointToV2,
  getAiMediaAsset,
  getAiMediaAssetSenders,
  getAiMediaAssets,
  getControlledAnswerCards,
  getDelayedResponseJobs,
  getDelayedResponsePolicies,
  getAiLeadQualificationConfig,
  initializeAiLeadQualificationFlow,
  getLeadQualificationReminders,
  previewDelayedResponsePolicy,
  previewLeadQualificationConfirmationCheckpointV2,
  updateDelayedResponsePolicy,
  updateAiBusinessProofLocation,
  updateAiLeadFlowField,
  updateAiLeadMediaTriggerRule,
  updateControlledAnswerCard,
  updateLeadQualificationConfirmationCheckpoint,
  updateLeadQualificationConfirmationCheckpointV2,
  updateLeadQualificationReminderPolicy,
  updateLeadQualificationReminderStep,
  activateLeadQualificationReminderStep,
} from "./api/aiAgentApi";

const CONCERN_WORDS = ["concern", "problem", "issue", "symptom"];

function isConcernLikeField(field) {
  return CONCERN_WORDS.some(w =>
    [field.fieldKey, field.displayName, field.validationType]
      .some(s => s?.toLowerCase().includes(w))
  );
}

const KNOWN_VALIDATION_TYPES = [
  "name", "city", "phone", "email", "number", "duration",
  "yes_no", "photo", "callback_time", "free_text",
  "concern", "problem", "issue", "symptom",
];

const CONTROLLED_REPLY_SCENARIOS = [
  "FieldWhyNeeded",
  "FieldUnavailable",
  "PrivacyConcern",
  "FieldObjection",
];

const CONTROLLED_REPLY_SCENARIO_HELP = {
  FieldWhyNeeded: "Used when customer asks why this field is needed.",
  PrivacyConcern: "Used when customer raises a privacy or safety concern.",
  FieldObjection: "Used when customer refuses or objects to answering.",
  FieldUnavailable: "Used when customer says they cannot answer now.",
};

const CONTROLLED_REPLY_MODES = ["Configured", "AiPolished"];
const REMINDER_MAX_COUNT_LIMIT = 10;
const REMINDER_MEDIA_PAGE_SIZE = 100;
const REMINDER_MEDIA_CAPTION_MAX_LENGTH = 1024;
const REMINDER_MEDIA_TYPES = new Set(["Image", "Document", "Video", "Audio"]);
const DELAYED_RESPONSE_TRIGGER_EVENTS = ["SummaryConfirmed", "FieldCollected"];
const DELAYED_RESPONSE_PAYLOAD_TYPES = [
  "TextOnly",
  "TextThenMedia",
  "MediaOnly",
  "MediaThenText",
];
const DELAYED_RESPONSE_REPEAT_POLICIES = [
  "OncePerSessionPolicyRevision",
  "OncePerSession",
  "EveryTrigger",
];
const DELAYED_RESPONSE_JOB_STATUSES = [
  "All",
  "Pending",
  "Processing",
  "Paused",
  "Sent",
  "Skipped",
  "Failed",
  "Cancelled",
];
const DELAYED_RESPONSE_MEDIA_PAYLOAD_TYPES = new Set([
  "TextThenMedia",
  "MediaOnly",
  "MediaThenText",
]);
const DELAYED_RESPONSE_TEXT_PAYLOAD_TYPES = new Set([
  "TextOnly",
  "TextThenMedia",
  "MediaThenText",
]);
const DELAYED_RESPONSE_MEDIA_ERROR_CODES = new Set([
  "MediaAssetMissing",
  "MediaAssetInactive",
  "MediaAssetNotApproved",
  "MediaConsentMissing",
  "MediaWhatsAppIdMissing",
  "MediaPhoneNumberMismatch",
  "MediaExpired",
  "UnsupportedMediaType",
  "MediaSendFailed",
]);
const CONFIRMATION_POLICIES = ["Never", "WhenLowConfidence", "Always"];
const DEFAULT_SUMMARY_CHECKPOINT_MESSAGE =
  "Ab tak mujhe ye details mili hain:\n\n{{summary}}\n\nKya ye details sahi hain?";
const LEGACY_SUMMARY_CHECKPOINT_WARNING =
  "Legacy compatibility policy. Current Yes/No outcomes are fixed. Convert this checkpoint to Version 2 to configure supported outcomes.";
const SUMMARY_CHECKPOINT_HELP_ITEMS = [
  {
    title: "Active + Confirmation Required",
    text: "If both are on, the bot can show the summary and ask for confirmation. If inactive or confirmation required is off, that checkpoint is skipped.",
  },
  {
    title: "Trigger After Field",
    text: "Summary opens only when this field is captured and the next field also matches Before Field.",
  },
  {
    title: "Before Field",
    text: "The resolved next lead field must match this field. End-of-flow placement is not supported yet.",
  },
  {
    title: "Included Fields",
    text: "Only selected collected fields appear in the summary, and empty or missing values are skipped safely.",
  },
  {
    title: "Current Yes behavior",
    text: "Yes uses the legacy completion and existing delayed follow-up/wait behavior.",
  },
  {
    title: "Current No behavior",
    text: "With Allow correction enabled, No enters guided correction. Without it, No uses the legacy team handover behavior. Configurable Yes/No outcomes will be added later.",
  },
];
const SUMMARY_PLACEMENT_MODES = [
  "AfterField",
  "BeforeField",
  "AfterFieldBeforeField",
  "EndOfFlow",
];
const SUMMARY_YES_ACTIONS = [
  "ContinueToNextField",
  "AskConfiguredField",
  "Handover",
  "CompleteLead",
  "Wait",
];
const SUMMARY_NO_ACTIONS = [
  "AskConfiguredField",
  "Handover",
  "CompleteLead",
  "Wait",
  "KeepPending",
  "AskForCorrection",
];
const SUMMARY_ACTIONS_REQUIRING_FIELD = new Set(["AskConfiguredField"]);
const DEFAULT_V2_SUMMARY_TEMPLATE = "Please review these details:\n\n{{summary}}";
const DEFAULT_V2_CONFIRMATION_QUESTION = "Are these details correct?";

const EMPTY_REMINDER_POLICY_FORM = {
  isEnabled: false,
  maxReminderCount: 1,
  respectBusinessHours: false,
  timezone: "",
};

const EMPTY_REMINDER_STEP_FORM = {
  id: null,
  sequence: 1,
  delayMinutes: 30,
  messageText: "",
  includePendingQuestion: true,
  isActive: true,
  optionalMediaAssetId: null,
  optionalMediaCaption: "",
  useTemplateOutsideWindow: false,
  whatsAppTemplateName: "",
  templateLanguage: "",
  templateVariablesJson: "",
};

const EMPTY_DELAYED_RESPONSE_POLICY_FORM = {
  id: null,
  policyRevision: 1,
  name: "",
  triggerEvent: "SummaryConfirmed",
  triggerFieldKey: "",
  triggerCheckpointId: "",
  triggerAction: "",
  delaySeconds: 120,
  sortOrder: 100,
  repeatPolicy: "OncePerSessionPolicyRevision",
  maxSendsPerSession: 1,
  payloadType: "TextOnly",
  textTemplate: "",
  mediaAssetId: "",
  guardRulesJson: "",
  cancellationPolicyJson: "",
  isActive: false,
};

const EMPTY_DELAYED_RESPONSE_SAMPLE_VALUES = {
  customerName: "",
  businessName: "",
  summary: "",
  fields: {},
};

function tryParseHints(json) {
  if (!json || !json.trim()) return { ok: true, hints: {} };
  try {
    const parsed = JSON.parse(json);
    return { ok: true, hints: (parsed && typeof parsed === "object" && !Array.isArray(parsed)) ? parsed : {} };
  } catch {
    return { ok: false, hints: {} };
  }
}

function hintsToJson(existingJson, valueAliasRows) {
  const { hints: base } = tryParseHints(existingJson);
  const validRows = valueAliasRows.filter(r => r.key.trim() !== "");
  const merged = { ...base };
  if (validRows.length > 0) {
    merged.valueAliases = Object.fromEntries(
      validRows.map(r => [r.key.trim(), r.value.trim()])
    );
  } else {
    delete merged.valueAliases;
  }
  return Object.keys(merged).length === 0 ? null : JSON.stringify(merged, null, 2);
}

function parseFieldKeysJson(json) {
  try {
    const parsed = JSON.parse(json || "[]");
    return Array.isArray(parsed)
      ? parsed.filter(key => typeof key === "string" && key.trim() !== "")
      : [];
  } catch {
    return [];
  }
}

function parseJsonObject(json, fallback = {}) {
  if (!json || !String(json).trim()) return fallback;
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : fallback;
  } catch {
    return fallback;
  }
}

function delayedResponsePayloadFromPolicy(policy) {
  const payload = parseJsonObject(policy?.payloadJson, {});
  return {
    payloadType: payload.payloadType || "TextOnly",
    textTemplate: payload.textTemplate || "",
    mediaAssetId: payload.mediaAssetId || "",
  };
}

function DelayedResponsePolicyToForm(policy) {
  const payload = delayedResponsePayloadFromPolicy(policy);
  return {
    ...EMPTY_DELAYED_RESPONSE_POLICY_FORM,
    id: policy?.id || null,
    policyRevision: Number(policy?.policyRevision) || 1,
    name: policy?.name || "",
    triggerEvent: policy?.triggerEvent || "SummaryConfirmed",
    triggerFieldKey: policy?.triggerFieldKey || "",
    triggerCheckpointId: policy?.triggerCheckpointId || "",
    triggerAction: policy?.triggerAction || "",
    delaySeconds: Number(policy?.delaySeconds) || 0,
    sortOrder: Number(policy?.sortOrder) || 0,
    repeatPolicy: policy?.repeatPolicy || "OncePerSessionPolicyRevision",
    maxSendsPerSession: Number(policy?.maxSendsPerSession) || 1,
    payloadType: payload.payloadType,
    textTemplate: payload.textTemplate,
    mediaAssetId: payload.mediaAssetId,
    guardRulesJson: isBlankJson(policy?.guardRulesJson) ? "" : policy.guardRulesJson,
    cancellationPolicyJson: isBlankJson(policy?.cancellationPolicyJson)
      ? ""
      : policy.cancellationPolicyJson,
    isActive: Boolean(policy?.isActive),
  };
}

function buildDelayedResponsePayloadJson(form) {
  const payload = {
    payloadType: form.payloadType || "TextOnly",
  };
  if (DELAYED_RESPONSE_TEXT_PAYLOAD_TYPES.has(payload.payloadType)) {
    payload.textTemplate = String(form.textTemplate || "").trim();
  }
  if (DELAYED_RESPONSE_MEDIA_PAYLOAD_TYPES.has(payload.payloadType)) {
    payload.mediaAssetId = String(form.mediaAssetId || "").trim();
  }
  return JSON.stringify(payload);
}

function buildDelayedResponsePolicyPayload(form, activeFlowId) {
  const triggerEvent = form.triggerEvent || "SummaryConfirmed";
  return {
    flowDefinitionId: activeFlowId,
    name: String(form.name || "").trim(),
    triggerEvent,
    triggerFieldKey:
      triggerEvent === "FieldCollected"
        ? String(form.triggerFieldKey || "").trim() || null
        : null,
    triggerCheckpointId: String(form.triggerCheckpointId || "").trim() || null,
    triggerAction: String(form.triggerAction || "").trim() || null,
    delaySeconds: Number(form.delaySeconds) || 0,
    sortOrder: Number(form.sortOrder) || 0,
    guardRulesJson: String(form.guardRulesJson || "").trim() || null,
    payloadJson: buildDelayedResponsePayloadJson(form),
    cancellationPolicyJson: String(form.cancellationPolicyJson || "").trim() || null,
    repeatPolicy: form.repeatPolicy || "OncePerSessionPolicyRevision",
    maxSendsPerSession: Number(form.maxSendsPerSession) || 1,
    isActive: Boolean(form.isActive),
  };
}

function isBlankJson(value) {
  const trimmed = String(value || "").trim();
  return !trimmed || trimmed === "{}" || trimmed === "[]";
}

const EMPTY_FIELD_FORM = {
  fieldKey: "",
  displayName: "",
  questionText: "",
  resumeQuestionText: "",
  isRequired: true,
  sortOrder: 0,
  validationType: "",
  confirmationPolicy: "Never",
  extractionHintsJson: "",
  isActive: true,
};

const EMPTY_CHECKPOINT_FORM = {
  id: null,
  policyVersion: 1,
  policyRevision: 1,
  convertSourceCheckpointId: null,
  name: "",
  placementMode: "AfterFieldBeforeField",
  triggerAfterFieldKey: "",
  beforeFieldKey: "",
  includedFieldKeys: [],
  messageTemplate: DEFAULT_SUMMARY_CHECKPOINT_MESSAGE,
  summaryTemplate: DEFAULT_V2_SUMMARY_TEMPLATE,
  confirmationQuestionText: DEFAULT_V2_CONFIRMATION_QUESTION,
  confirmationRequired: true,
  allowEdit: true,
  allowCorrection: true,
  correctionPromptText: "Which detail should be updated?",
  yesResponseText: "",
  yesAction: "ContinueToNextField",
  yesActionFieldKey: "",
  noResponseText: "",
  noAction: "AskForCorrection",
  noActionFieldKey: "",
  isActive: true,
  sortOrder: 1,
};

const EMPTY_RULE_FORM = {
  triggerName: "",
  phoneNumberId: "",
  triggerType: "IntentBased",
  triggerPurpose: "PrimaryAction",
  repeatPolicy: "OncePerSession",
  intent: "",
  step: "",
  eventType: "",
  requiredFieldsJson: "",
  mediaCategory: "Guide",
  useCase: "",
  mediaType: "",
  maxAssets: 1,
  priority: 0,
  requiresConsent: false,
  isActive: true,
  delaySeconds: 0,
};

const EMPTY_PROOF_FORM = {
  locationName: "",
  district: "",
  state: "",
  country: "",
  aliasesJson: "",
  nearbyAreasJson: "",
  approvedWordingTemplate: "",
  disclaimer: "",
  isActive: true,
};

const EMPTY_CONTROLLED_REPLY_FORM = {
  id: null,
  title: "",
  scenarioTags: [],
  approvedAnswer: "",
  replyMode: "Configured",
  mustNotSayText: "",
  priority: 0,
  isActive: true,
};

const STEPS = [
  { id: "overview", title: "Overview", blurb: "Check the current flow and sending settings for this business.", icon: Activity },
  { id: "questions", title: "Questions", blurb: "Choose the details to collect and the order your assistant asks for them.", icon: ListChecks },
  { id: "confirmation", title: "Confirmation", blurb: "Let customers review their details before the flow continues.", icon: MessageSquare, optional: true },
  { id: "followups", title: "Follow-ups", blurb: "Remind customers who stop replying, or configure a reply after a flow event.", icon: Clock, optional: true },
  { id: "media", title: "Media rules", blurb: "Choose when approved brochures, images and other supported files can be sent.", icon: ImageIcon, optional: true },
  { id: "proof", title: "Regional examples", blurb: "Configure approved examples for customers in a matching location.", icon: MapPin, optional: true },
];

function Section({ sectionId, title, children, actions }) {
  return (
    <section
      aria-labelledby={`${sectionId}-heading`}
      className="border-b border-slate-200 pb-6 last:border-b-0 last:pb-0"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2
          id={`${sectionId}-heading`}
          className="text-sm font-semibold text-slate-900"
        >
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Modal({ title, children, onClose, error, onRetry }) {
  const dialogRef = useAiAgentDialog(onClose);
  const titleId = React.useId();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto p-5">
          <AiLoadError message={error} onRetry={onRetry} label="dialog data" />
          {children}
        </div>
      </div>
    </div>
  );
}

function TextInput({ label, help, value, onChange, name, multiline = false, rows = 4, maxLength, disabled = false, type = "text" }) {
  const id = React.useId();
  const inputProps = { id, disabled, maxLength, value, onChange: event => onChange(name, event.target.value),
    "aria-describedby": help ? `${id}-help` : undefined,
    className: "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50" };
  return <div className="text-sm text-slate-700">
    <label htmlFor={id}>{label}</label>
    {help ? <p id={`${id}-help`} className="mt-0.5 text-xs text-slate-500">{help}</p> : null}
    {multiline ? <textarea {...inputProps} rows={rows} /> : <input {...inputProps} type={type} />}
  </div>;
}

const OPTION_LABELS = {
  name: "Name", city: "City", phone: "Phone number", email: "Email address", number: "Number", duration: "Duration", yes_no: "Yes / No", photo: "Photo", callback_time: "Callback time", free_text: "Free text", concern: "Concern", problem: "Problem", issue: "Issue", symptom: "Symptom",
  ContinueToNextField: "Continue to the next question", AskConfiguredField: "Ask a specific question", Wait: "Wait for the customer",
  Never: "Do not ask for confirmation", WhenLowConfidence: "Only when the answer is uncertain", Always: "Always ask for confirmation",
  AfterField: "After an answer", BeforeField: "Before a question", AfterFieldBeforeField: "Between two questions", EndOfFlow: "At the end of collection",
  CompleteLead: "Complete the enquiry", Handover: "Hand over to your team", AskForCorrection: "Ask the customer to correct details", KeepPending: "Keep the enquiry pending",
};

function SelectInput({ label, value, onChange, name, options, allowBlank = false, optionLabels = {} }) {
  return (
    <label className="block text-sm text-slate-700">
      {label}
      <select
        value={value}
        onChange={event => onChange(name, event.target.value)}
        className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
      >
        {allowBlank ? <option value="">Not set</option> : null}
        {options.map(option => (
          <option key={option} value={option}>
            {optionLabels[option] || OPTION_LABELS[option] || option}
          </option>
        ))}
      </select>
    </label>
  );
}

function BoolBadge({ value, trueLabel = "Yes", falseLabel = "No" }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
        value
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function FieldToForm(field) {
  return {
    displayName: field?.displayName || "",
    questionText: field?.questionText || "",
    resumeQuestionText: field?.resumeQuestionText || "",
    isRequired: Boolean(field?.isRequired),
    sortOrder: Number(field?.sortOrder) || 0,
    validationType: field?.validationType || "",
    confirmationPolicy: field?.confirmationPolicy || "Never",
    extractionHintsJson: field?.extractionHintsJson || "",
    isActive: field?.isActive !== false,
  };
}

function CheckpointToForm(checkpoint) {
  const effective = checkpoint?.effectivePolicy || {};
  const policyVersion = Number(checkpoint?.policyVersion || effective?.policyVersion || 1);
  return {
    id: checkpoint?.id || null,
    policyVersion,
    policyRevision: Number(checkpoint?.policyRevision || effective?.policyRevision || 1),
    convertSourceCheckpointId: null,
    name: checkpoint?.name || "",
    placementMode: effective?.placementMode || checkpoint?.placementMode || "AfterFieldBeforeField",
    triggerAfterFieldKey: checkpoint?.triggerAfterFieldKey || "",
    beforeFieldKey: checkpoint?.beforeFieldKey || "",
    includedFieldKeys: parseFieldKeysJson(checkpoint?.includedFieldKeysJson),
    messageTemplate: checkpoint?.messageTemplate || DEFAULT_SUMMARY_CHECKPOINT_MESSAGE,
    summaryTemplate: effective?.summaryTemplate || checkpoint?.summaryTemplate || DEFAULT_V2_SUMMARY_TEMPLATE,
    confirmationQuestionText:
      effective?.confirmationQuestionText ||
      checkpoint?.confirmationQuestionText ||
      DEFAULT_V2_CONFIRMATION_QUESTION,
    confirmationRequired: checkpoint?.confirmationRequired !== false,
    allowEdit: checkpoint?.allowEdit !== false,
    allowCorrection:
      effective?.allowCorrection ??
      checkpoint?.allowCorrection ??
      (checkpoint?.allowEdit !== false),
    correctionPromptText:
      effective?.correctionPromptText ||
      checkpoint?.correctionPromptText ||
      "Which detail should be updated?",
    yesResponseText: effective?.yesResponseText || checkpoint?.yesResponseText || "",
    yesAction: effective?.yesAction || checkpoint?.yesAction || "ContinueToNextField",
    yesActionFieldKey: effective?.yesActionFieldKey || checkpoint?.yesActionFieldKey || "",
    noResponseText: effective?.noResponseText || checkpoint?.noResponseText || "",
    noAction: effective?.noAction || checkpoint?.noAction || "AskForCorrection",
    noActionFieldKey: effective?.noActionFieldKey || checkpoint?.noActionFieldKey || "",
    isActive: checkpoint?.isActive !== false,
    sortOrder: Number(checkpoint?.sortOrder) || 1,
  };
}

function CheckpointToV2ConversionForm(checkpoint) {
  const base = CheckpointToForm(checkpoint);
  return {
    ...EMPTY_CHECKPOINT_FORM,
    ...base,
    id: null,
    policyVersion: 2,
    policyRevision: 1,
    convertSourceCheckpointId: checkpoint?.id || null,
    name: checkpoint?.name ? `${checkpoint.name} V2` : "Version 2 summary policy",
    summaryTemplate: "",
    confirmationQuestionText: "",
    allowCorrection: base.allowEdit,
    correctionPromptText: base.allowEdit ? "Which detail should be updated?" : "",
    yesAction: "ContinueToNextField",
    yesResponseText: "",
    yesActionFieldKey: "",
    noAction: base.allowEdit ? "AskForCorrection" : "Handover",
    noResponseText: "",
    noActionFieldKey: "",
    isActive: false,
  };
}

function summaryCheckpointOutcomeLabel(action) {
  switch (action) {
    case "LegacyCompleteAndExistingDelayedFollowUpWait":
      return "Legacy completion and existing delayed follow-up/wait";
    case "LegacyHandover":
      return "Legacy handover";
    case "AskForCorrection":
      return "Ask for correction";
    case "ContinueToNextField":
      return "Continue to next field";
    case "AskConfiguredField":
      return "Ask configured field";
    case "CompleteLead":
      return "Complete lead";
    case "Wait":
      return "Send configured response and wait";
    case "KeepPending":
      return "Keep pending";
    default:
      return action || "Not configured";
  }
}

function summaryCheckpointEffectivePolicy(checkpoint) {
  const policy = checkpoint?.effectivePolicy || {};
  const allowCorrection = checkpoint?.allowEdit === true;
  return {
    policyVersion: Number(checkpoint?.policyVersion || policy?.policyVersion || 1),
    placementMode:
      policy?.placementMode || checkpoint?.placementMode || "AfterFieldBeforeField",
    yesAction:
      policy?.yesAction || "LegacyCompleteAndExistingDelayedFollowUpWait",
    noAction:
      policy?.noAction || (allowCorrection ? "AskForCorrection" : "LegacyHandover"),
    executionStatus: policy?.executionStatus || "LegacyRuntimeActive",
    capabilities: checkpoint?.capabilities || {},
    warnings: Array.isArray(checkpoint?.validationWarnings)
      ? checkpoint.validationWarnings
      : [],
  };
}

function RuleToForm(rule) {
  return {
    triggerName: rule?.triggerName || "",
    phoneNumberId: rule?.phoneNumberId || "",
    triggerType: rule?.triggerType || "IntentBased",
    triggerPurpose: rule?.triggerPurpose || "PrimaryAction",
    repeatPolicy: rule?.repeatPolicy || "OncePerSession",
    intent: rule?.intent || "",
    step: rule?.step || "",
    eventType: rule?.eventType || "",
    requiredFieldsJson: rule?.requiredFieldsJson || "",
    mediaCategory: rule?.mediaCategory || "Guide",
    useCase: rule?.useCase || "",
    mediaType: rule?.mediaType || "",
    maxAssets: Number(rule?.maxAssets) || 1,
    priority: Number(rule?.priority) || 0,
    requiresConsent: Boolean(rule?.requiresConsent),
    isActive: rule?.isActive !== false,
    delaySeconds: Number(rule?.delaySeconds) || 0,
  };
}

function ControlledReplyToForm(card) {
  return {
    id: card?.id || null,
    title: card?.title || "",
    scenarioTags: Array.isArray(card?.scenarioTags) ? card.scenarioTags : [],
    approvedAnswer: card?.approvedAnswer || "",
    replyMode: CONTROLLED_REPLY_MODES.includes(card?.replyMode)
      ? card.replyMode
      : "Configured",
    mustNotSayText: Array.isArray(card?.mustNotSay)
      ? card.mustNotSay.join("\n")
      : "",
    priority: Number(card?.priority) || 0,
    isActive: card?.isActive !== false,
  };
}

function ReminderPolicyToForm(policy) {
  const maxReminderCount = Number(policy?.maxReminderCount);
  return {
    isEnabled: Boolean(policy?.isEnabled),
    maxReminderCount: Number.isFinite(maxReminderCount) ? maxReminderCount : 1,
    respectBusinessHours: Boolean(policy?.respectBusinessHours),
    timezone: policy?.timezone || "",
  };
}

function ReminderStepToForm(step) {
  return {
    id: step?.id || null,
    sequence: Number(step?.sequence) || 1,
    delayMinutes: Number(step?.delayMinutes) || 30,
    messageText: step?.messageText || "",
    includePendingQuestion: step?.includePendingQuestion !== false,
    isActive: step?.isActive !== false,
    optionalMediaAssetId: step?.optionalMediaAssetId || null,
    optionalMediaCaption: step?.optionalMediaCaption || "",
    useTemplateOutsideWindow: Boolean(step?.useTemplateOutsideWindow),
    whatsAppTemplateName: step?.whatsAppTemplateName || "",
    templateLanguage: step?.templateLanguage || "",
    templateVariablesJson: step?.templateVariablesJson || "",
  };
}

function normalizeReminderMediaType(value) {
  const type = String(value || "").trim();
  return REMINDER_MEDIA_TYPES.has(type) ? type : "";
}

function parseListInput(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map(item => item.trim())
    .filter(Boolean);
}

function shortPreview(value, maxLength = 120) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function AiLeadQualificationConfigPage() {
  const { search } = useLocation();
  const { can, hasAllAccess } = useAuth() || {};
  const canManage =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_AUTO_REPLY_MANAGE));

  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [loadError, setLoadError] = React.useState("");
  const [setupError, setSetupError] = React.useState("");
  const [initializingFlow, setInitializingFlow] = React.useState(false);
  const setupInFlight = React.useRef(false);
  const [selectedExistingFlowId, setSelectedExistingFlowId] = React.useState("");
  const [fieldFilter, setFieldFilter] = React.useState("Active");
  const [fieldError, setFieldError] = React.useState("");
  const [savingField, setSavingField] = React.useState(false);
  const [savingRule, setSavingRule] = React.useState(false);
  const [savingCheckpoint, setSavingCheckpoint] = React.useState(false);
  const [config, setConfig] = React.useState(null);
  const [controlledCards, setControlledCards] = React.useState([]);
  const [reminderConfig, setReminderConfig] = React.useState(null);
  const [reminderPolicyForm, setReminderPolicyForm] = React.useState(EMPTY_REMINDER_POLICY_FORM);
  const [reminderSteps, setReminderSteps] = React.useState([]);
  const [reminderMediaAssets, setReminderMediaAssets] = React.useState([]);
  const [reminderMediaAssetsLoading, setReminderMediaAssetsLoading] = React.useState(false);
  const [reminderMediaAssetsError, setReminderMediaAssetsError] = React.useState("");
  const [selectedReminderMediaAsset, setSelectedReminderMediaAsset] = React.useState(null);
  const [delayedResponsePolicies, setDelayedResponsePolicies] = React.useState([]);
  const [delayedResponseJobs, setDelayedResponseJobs] = React.useState([]);
  const [delayedResponsePolicyForm, setDelayedResponsePolicyForm] = React.useState(
    EMPTY_DELAYED_RESPONSE_POLICY_FORM
  );
  const [checkpointEditorOpen, setCheckpointEditorOpen] = React.useState(false);
  const [delayedEditorOpen, setDelayedEditorOpen] = React.useState(false);
  const [delayedResponsePreview, setDelayedResponsePreview] = React.useState(null);
  const [delayedResponsePreviewError, setDelayedResponsePreviewError] = React.useState("");
  const [delayedResponseSampleValues, setDelayedResponseSampleValues] = React.useState(
    EMPTY_DELAYED_RESPONSE_SAMPLE_VALUES
  );
  const [delayedResponseJobStatusFilter, setDelayedResponseJobStatusFilter] =
    React.useState("All");
  const [savingDelayedResponsePolicy, setSavingDelayedResponsePolicy] =
    React.useState(false);
  const [previewingDelayedResponsePolicy, setPreviewingDelayedResponsePolicy] =
    React.useState(false);
  const [loadingDelayedResponses, setLoadingDelayedResponses] = React.useState(false);
  const [editingReminderStep, setEditingReminderStep] = React.useState(null);
  const [reminderStepForm, setReminderStepForm] = React.useState(EMPTY_REMINDER_STEP_FORM);
  const [savingReminderPolicy, setSavingReminderPolicy] = React.useState(false);
  const [savingReminderStep, setSavingReminderStep] = React.useState(false);
  const [editingField, setEditingField] = React.useState(null);
  const [editingRule, setEditingRule] = React.useState(null);
  const [editingProof, setEditingProof] = React.useState(null);
  const [editingControlledReply, setEditingControlledReply] = React.useState(null);
  const [fieldForm, setFieldForm] = React.useState(EMPTY_FIELD_FORM);
  const [checkpointForm, setCheckpointForm] = React.useState(EMPTY_CHECKPOINT_FORM);
  const [checkpointPreview, setCheckpointPreview] = React.useState(null);
  const [checkpointSampleValues, setCheckpointSampleValues] = React.useState({});
  const [previewingCheckpoint, setPreviewingCheckpoint] = React.useState(false);
  const [ruleForm, setRuleForm] = React.useState(EMPTY_RULE_FORM);
  const [proofForm, setProofForm] = React.useState(EMPTY_PROOF_FORM);
  const [controlledReplyForm, setControlledReplyForm] = React.useState(EMPTY_CONTROLLED_REPLY_FORM);
  const [savingProof, setSavingProof] = React.useState(false);
  const [savingControlledReply, setSavingControlledReply] = React.useState(false);
  const [valueAliasRows, setValueAliasRows] = React.useState([]);
  const [hintsParseWarning, setHintsParseWarning] = React.useState(false);
  const [showJsonPreview, setShowJsonPreview] = React.useState(false);
  const [sectionErrors, setSectionErrors] = React.useState({});
  const [cardsLoading, setCardsLoading] = React.useState(false);
  const [reminderConfigLoading, setReminderConfigLoading] = React.useState(false);
  const [reminderLoadedFlow, setReminderLoadedFlow] = React.useState(null);
  const [senderRetry, setSenderRetry] = React.useState(0);
  const sectionError = React.useCallback((key, message) => setSectionErrors(prev => ({...prev, [key]: message})), []);
  const request = useAiRequestGuard({reminders: config?.activeFlow?.id, delayedPreview: [delayedResponsePolicyForm, delayedResponseSampleValues], checkpointPreview: [checkpointForm, checkpointSampleValues]});
  const [senderOptions, setSenderOptions] = React.useState([]);
  const [senderError, setSenderError] = React.useState("");
  React.useEffect(() => {
    const current = request("senders"); setSenderError(""); setSenderOptions([]);
    const loadSenders = async () => {
      try { const rows = await getAiMediaAssetSenders(); if (current()) setSenderOptions(normalizeSenderList(rows).filter(row => row.isActive)); }
      catch { if (current()) setSenderError("The WhatsApp sender list could not be loaded. Existing sender values are preserved in Advanced."); }
    };
    loadSenders();
  }, [request, senderRetry]);
  const [showControlledReplies, setShowControlledReplies] = React.useState(false);
  const [currentStepId, setCurrentStepId] = React.useState(() => initialAiAgentSection(STEPS, "overview", search));
  const [visitedStepIds, setVisitedStepIds] = React.useState(
    () => new Set(["overview"])
  );

  React.useEffect(() => {
    const id = initialAiAgentSection(STEPS, "overview", search);
    setCurrentStepId(id);
    setVisitedStepIds(prev => new Set([...prev, id]));
  }, [search]);

  const selectStep = id => {
    setCurrentStepId(id);
    setVisitedStepIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const currentStepIndex = STEPS.findIndex(step => step.id === currentStepId);
  const currentStep = STEPS[currentStepIndex] || STEPS[0];
  const isLastStep = currentStepIndex === STEPS.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const goNext = () => {
    if (isLastStep) return;
    selectStep(STEPS[currentStepIndex + 1].id);
  };
  const goBack = () => {
    if (isFirstStep) return;
    selectStep(STEPS[currentStepIndex - 1].id);
  };

  const loadControlledCards = React.useCallback(async () => {
    const current = request("cards"); sectionError("cards", ""); setCardsLoading(true); setControlledCards([]);
    try { const cards = await getControlledAnswerCards(); if (current()) setControlledCards(Array.isArray(cards) ? cards : []); }
    catch (error) { if (current()) sectionError("cards", error?.response?.data?.message || "Controlled replies could not be loaded."); }
    finally { if (current()) setCardsLoading(false); }
  }, [request, sectionError]);

  const loadReminderConfig = React.useCallback(async flowDefinitionId => {
    const current = request("reminders"); sectionError("reminders", ""); setReminderConfig(null); setReminderLoadedFlow(null); setReminderSteps([]);
    if (!flowDefinitionId) {
      setReminderConfig(null);
      setReminderPolicyForm(EMPTY_REMINDER_POLICY_FORM);
      setReminderSteps([]); setReminderConfigLoading(false);
      return;
    }
    setReminderConfigLoading(true);
    try {
      const response = await getLeadQualificationReminders(flowDefinitionId);
      if (!current()) return;
      setReminderLoadedFlow(flowDefinitionId);
      setReminderConfig(response || null);
      setReminderPolicyForm(ReminderPolicyToForm(response?.policy));
      setReminderSteps(Array.isArray(response?.steps) ? response.steps : []);
    } catch (error) {
      if (!current()) return;
      setReminderConfig(null);
      setReminderPolicyForm(EMPTY_REMINDER_POLICY_FORM);
      setReminderSteps([]);
      sectionError("reminders", error?.response?.data?.message || "Reminder configuration is unavailable. Retry before editing or saving it.");
    } finally { if (current()) setReminderConfigLoading(false); }
  }, [request, sectionError]);
  React.useEffect(() => { loadReminderConfig(config?.activeFlow?.id || null); }, [config?.activeFlow?.id, loadReminderConfig]);

  const loadReminderMediaAssets = React.useCallback(async () => {
    const current = request("media"); setReminderMediaAssets([]);
    setReminderMediaAssetsLoading(true);
    setReminderMediaAssetsError("");
    try {
      const result = await getAiMediaAssets({
        page: 1,
        pageSize: REMINDER_MEDIA_PAGE_SIZE,
        isActive: true,
        approvedForAutoSend: true,
      });
      if (!current()) return;
      const items = Array.isArray(result?.items) ? result.items : [];
      const eligible = items.filter(asset => {
        const mediaType = normalizeReminderMediaType(asset?.mediaType);
        const notExpired =
          !asset?.expiresAtUtc || new Date(asset.expiresAtUtc).getTime() > Date.now();
        return (
          mediaType &&
          REMINDER_MEDIA_TYPES.has(mediaType) &&
          Boolean(asset?.isActive) &&
          Boolean(asset?.approvedForAutoSend) &&
          asset?.requiresHumanApproval === false &&
          (!["beforeafter", "testimonial", "casestudy"].includes(String(asset?.category || "").toLowerCase()) || asset?.consentConfirmed === true) &&
          Boolean(asset?.phoneNumberId) &&
          notExpired
        );
      });
      setReminderMediaAssets(eligible);
    } catch (error) {
      if (!current()) return;
      setReminderMediaAssets([]);
      setReminderMediaAssetsError(
        error?.response?.data?.message ||
          "Approved reminder media assets could not be loaded. Text-only reminder editing is still available."
      );
    } finally {
      if (current()) setReminderMediaAssetsLoading(false);
    }
  }, [request]);

  const loadReminderMediaAssetDetail = React.useCallback(async assetId => {
    const current = request("mediaDetail"); setSelectedReminderMediaAsset(null); sectionError("mediaDetail", "");
    if (!assetId) {
      setSelectedReminderMediaAsset(null);
      return;
    }

    try {
      const detail = await getAiMediaAsset(assetId);
      if (current()) setSelectedReminderMediaAsset(detail || null);
    } catch (error) { if (current()) sectionError("mediaDetail", error?.response?.data?.message || "Media details could not be loaded. Sender compatibility is not verified."); }
  }, [request, sectionError]);

  const loadDelayedResponses = React.useCallback(async () => {
    const current = request("delayed"); sectionError("delayed", ""); setDelayedResponsePolicies([]); setDelayedResponseJobs([]);
    setLoadingDelayedResponses(true);
    try {
      const [policies, jobs] = await Promise.all([
        getDelayedResponsePolicies(),
        getDelayedResponseJobs(),
      ]);
      if (!current()) return;
      setDelayedResponsePolicies(Array.isArray(policies) ? policies : []);
      setDelayedResponseJobs(Array.isArray(jobs) ? jobs : []);
    } catch (error) {
      if (!current()) return;
      setDelayedResponsePolicies([]);
      setDelayedResponseJobs([]);
      sectionError("delayed",
        error?.response?.data?.message ||
          "Delayed response policies and jobs could not be loaded."
      );
    } finally {
      if (current()) setLoadingDelayedResponses(false);
    }
  }, [request, sectionError]);

  const load = React.useCallback(async ({ silent = false } = {}) => {
    const current = request("config");
    setLoadError("");
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getAiLeadQualificationConfig();
      if (!current()) return;
      setConfig(response);

      await Promise.all([loadControlledCards(), loadReminderMediaAssets(), loadDelayedResponses()]);
    } catch (error) {
      if (!current()) return;
      setLoadError(error?.response?.data?.message || "Failed to load lead qualification config.");
      toast.error(
        error?.response?.data?.message ||
          "Failed to load lead qualification config."
      );
    } finally {
      if (current()) { setLoading(false); setRefreshing(false); }
    }
  }, [loadControlledCards, loadReminderMediaAssets, loadDelayedResponses, request]);

  React.useEffect(() => {
    load();
  }, [load]);

  const onFieldChange = (name, value) => {
    setFieldForm(prev => ({ ...prev, [name]: value }));
  };

  const onRuleChange = (name, value) => {
    setRuleForm(prev => ({ ...prev, [name]: value }));
  };

  const onDelayedResponsePolicyChange = (name, value) => {
    setPreviewingDelayedResponsePolicy(false); setDelayedResponsePreviewError("");
    setDelayedResponsePreview(null);
    setDelayedResponsePolicyForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === "triggerEvent" && value !== "FieldCollected") {
        next.triggerFieldKey = "";
      }
      if (name === "payloadType") {
        if (!DELAYED_RESPONSE_TEXT_PAYLOAD_TYPES.has(value)) next.textTemplate = "";
        if (!DELAYED_RESPONSE_MEDIA_PAYLOAD_TYPES.has(value)) next.mediaAssetId = "";
      }
      return next;
    });
    setDelayedResponsePreview(null);
  };

  const onDelayedResponseSampleChange = (name, value) => {
    setPreviewingDelayedResponsePolicy(false); setDelayedResponsePreviewError("");
    setDelayedResponsePreview(null);
    setDelayedResponseSampleValues(prev => ({ ...prev, [name]: value }));
  };

  const onDelayedResponseSampleFieldChange = (fieldKey, value) => {
    setPreviewingDelayedResponsePolicy(false); setDelayedResponsePreviewError("");
    setDelayedResponsePreview(null);
    setDelayedResponseSampleValues(prev => ({
      ...prev,
      fields: {
        ...(prev.fields || {}),
        [fieldKey]: value,
      },
    }));
  };

  const onCheckpointChange = (name, value) => {
    setCheckpointPreview(null); setPreviewingCheckpoint(false);
    setCheckpointForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === "placementMode") {
        if (value === "AfterField") next.beforeFieldKey = "";
        if (value === "BeforeField") next.triggerAfterFieldKey = "";
        if (value === "EndOfFlow") {
          next.triggerAfterFieldKey = "";
          next.beforeFieldKey = "";
        }
      }
      if (name === "allowCorrection" && !value && next.noAction === "AskForCorrection") {
        next.noAction = "Handover";
        next.correctionPromptText = "";
      }
      return next;
    });
    if (checkpointForm.policyVersion === 2) {
      setCheckpointPreview(null);
    }
  };

  const toggleCheckpointIncludedField = fieldKey => {
    setCheckpointForm(prev => {
      const current = new Set(prev.includedFieldKeys || []);
      if (current.has(fieldKey)) {
        current.delete(fieldKey);
      } else {
        current.add(fieldKey);
      }
      return { ...prev, includedFieldKeys: Array.from(current) };
    });
  };

  const openFieldEditor = field => {
    setFieldError("");
    setEditingField(field);
    setFieldForm(FieldToForm(field));
    setEditingControlledReply(null);
    setControlledReplyForm(EMPTY_CONTROLLED_REPLY_FORM);
    setShowControlledReplies(false);
    const { ok, hints } = tryParseHints(field.extractionHintsJson);
    setHintsParseWarning(!ok && !!field.extractionHintsJson);
    const rows = Object.entries(hints.valueAliases || {}).map(([key, value]) => ({ key, value }));
    setValueAliasRows(rows);
    setShowJsonPreview(false);
  };

  const openFieldCreator = () => {
    setFieldError("");
    setEditingField({});
    setFieldForm(EMPTY_FIELD_FORM);
    setEditingControlledReply(null);
    setControlledReplyForm(EMPTY_CONTROLLED_REPLY_FORM);
    setShowControlledReplies(false);
    setValueAliasRows([]);
    setHintsParseWarning(false);
    setShowJsonPreview(false);
  };

  const openCheckpointEditor = checkpoint => {
    setCheckpointEditorOpen(true);
    setCheckpointForm(CheckpointToForm(checkpoint));
    setCheckpointPreview(null);
  };

  const openCheckpointCreator = () => {
    setCheckpointEditorOpen(true);
    setCheckpointForm(EMPTY_CHECKPOINT_FORM);
    setCheckpointPreview(null);
  };

  const openCheckpointV2Creator = () => {
    setCheckpointEditorOpen(true);
    setCheckpointForm({
      ...EMPTY_CHECKPOINT_FORM,
      policyVersion: 2,
      isActive: false,
    });
    setCheckpointPreview(null);
  };

  const openCheckpointV2Conversion = checkpoint => {
    setCheckpointEditorOpen(true);
    setCheckpointForm(CheckpointToV2ConversionForm(checkpoint));
    setCheckpointPreview(null);
  };

  const onCheckpointSampleChange = (fieldKey, value) => {
    setCheckpointPreview(null); setPreviewingCheckpoint(false);
    setCheckpointSampleValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  const openDelayedResponsePolicyCreator = () => {
    setDelayedEditorOpen(true);
    setDelayedResponsePolicyForm(EMPTY_DELAYED_RESPONSE_POLICY_FORM);
    setDelayedResponsePreview(null);
  };

  const openDelayedResponsePolicyEditor = policy => {
    setDelayedEditorOpen(true);
    setDelayedResponsePolicyForm(DelayedResponsePolicyToForm(policy));
    setDelayedResponsePreview(null);
  };

  const openRuleEditor = rule => {
    setEditingRule(rule);
    setRuleForm(RuleToForm(rule));
  };

  const openRuleCreator = () => {
    setEditingRule({});
    setRuleForm(EMPTY_RULE_FORM);
  };

  const onProofChange = (name, value) => {
    setProofForm(prev => ({ ...prev, [name]: value }));
  };

  const onControlledReplyChange = (name, value) => {
    setControlledReplyForm(prev => ({ ...prev, [name]: value }));
  };

  const onReminderPolicyChange = (name, value) => {
    setReminderPolicyForm(prev => ({ ...prev, [name]: value }));
  };

  const onReminderStepChange = (name, value) => {
    setReminderStepForm(prev => ({ ...prev, [name]: value,
      ...(name === "optionalMediaAssetId" && !value ? { optionalMediaCaption: "" } : {}) }));
    if (name === "optionalMediaAssetId") {
      const assetId = value || null;
      if (!assetId) {
        setSelectedReminderMediaAsset(null);
      } else {
        loadReminderMediaAssetDetail(assetId);
      }
    }
  };

  const toggleControlledReplyScenario = scenario => {
    setControlledReplyForm(prev => {
      const current = Array.isArray(prev.scenarioTags) ? prev.scenarioTags : [];
      const exists = current.includes(scenario);
      return {
        ...prev,
        scenarioTags: exists
          ? current.filter(item => item !== scenario)
          : [...current, scenario],
      };
    });
  };

  const openControlledReplyCreator = () => {
    setEditingControlledReply({});
    setControlledReplyForm(EMPTY_CONTROLLED_REPLY_FORM);
    setShowControlledReplies(true);
  };

  const openControlledReplyEditor = card => {
    setEditingControlledReply(card);
    setControlledReplyForm(ControlledReplyToForm(card));
    setShowControlledReplies(true);
  };

  const openProofEditor = location => {
    setEditingProof(location);
    setProofForm({
      locationName: location.locationName || "",
      district: location.district || "",
      state: location.state || "",
      country: location.country || "",
      aliasesJson: location.aliasesJson || "",
      nearbyAreasJson: location.nearbyAreasJson || "",
      approvedWordingTemplate: location.approvedWordingTemplate || "",
      disclaimer: location.disclaimer || "",
      isActive: location.isActive !== false,
    });
  };

  const openProofCreator = () => {
    setEditingProof({}); 
    setProofForm(EMPTY_PROOF_FORM);
  };

  const openReminderStepCreator = () => {
    const maxSequence = reminderSteps.reduce(
      (max, step) => Math.max(max, Number(step?.sequence) || 0),
      0
    );
    setEditingReminderStep({});
    setReminderStepForm({
      ...EMPTY_REMINDER_STEP_FORM,
      sequence: maxSequence + 1,
    });
    setSelectedReminderMediaAsset(null);
    if (reminderMediaAssets.length === 0 && !reminderMediaAssetsLoading) {
      loadReminderMediaAssets();
    }
  };

  const openReminderStepEditor = step => {
    setEditingReminderStep(step);
    setReminderStepForm(ReminderStepToForm(step));
    setSelectedReminderMediaAsset(null);
    if (step?.optionalMediaAssetId) {
      loadReminderMediaAssetDetail(step.optionalMediaAssetId);
    }
    if (reminderMediaAssets.length === 0 && !reminderMediaAssetsLoading) {
      loadReminderMediaAssets();
    }
  };

  const saveProof = async () => {
    if (!editingProof) return;
    setSavingProof(true);
    const payload = {
      locationName: proofForm.locationName,
      district: proofForm.district || null,
      state: proofForm.state || null,
      country: proofForm.country || null,
      aliasesJson: proofForm.aliasesJson || null,
      nearbyAreasJson: proofForm.nearbyAreasJson || null,
      approvedWordingTemplate: proofForm.approvedWordingTemplate || null,
      disclaimer: proofForm.disclaimer || null,
      isActive: Boolean(proofForm.isActive),
    };
    try {
      if (editingProof.id) {
        await updateAiBusinessProofLocation(editingProof.id, payload);
        toast.success("Proof location updated.");
      } else {
        await createAiBusinessProofLocation(payload);
        toast.success("Proof location created.");
      }
      setEditingProof(null);
      await load({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (editingProof.id ? "Failed to update proof location." : "Failed to create proof location.")
      );
    } finally {
      setSavingProof(false);
    }
  };

  const deactivateProof = async location => {
    if (!window.confirm(`Deactivate "${location.locationName}"?`)) return;
    try {
      await deactivateAiBusinessProofLocation(location.id);
      toast.success("Proof location deactivated.");
      await load({ silent: true });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to deactivate proof location.");
    }
  };

  const saveControlledReply = async () => {
    if (!editingField?.fieldKey) {
      toast.error("Save the lead field before adding controlled replies.");
      return;
    }

    const title = String(controlledReplyForm.title || "").trim();
    const approvedAnswer = String(controlledReplyForm.approvedAnswer || "").trim();
    const scenarioTags = Array.isArray(controlledReplyForm.scenarioTags)
      ? controlledReplyForm.scenarioTags
      : [];
    const replyMode = CONTROLLED_REPLY_MODES.includes(controlledReplyForm.replyMode)
      ? controlledReplyForm.replyMode
      : "";

    if (!title) {
      toast.error("Controlled reply title is required.");
      return;
    }
    if (scenarioTags.length === 0) {
      toast.error("Select at least one controlled reply scenario.");
      return;
    }
    if (!approvedAnswer) {
      toast.error("Approved answer is required.");
      return;
    }
    if (!replyMode) {
      toast.error("Reply mode is required.");
      return;
    }

    const payload = {
      flowDefinitionId: activeFlow?.id || null,
      linkedFieldKey: editingField.fieldKey,
      title,
      scenarioTags,
      approvedAnswer,
      replyMode,
      mustNotSay: parseListInput(controlledReplyForm.mustNotSayText),
      priority: Number(controlledReplyForm.priority) || 0,
      isActive: Boolean(controlledReplyForm.isActive),
    };

    setSavingControlledReply(true);
    try {
      if (controlledReplyForm.id) {
        await updateControlledAnswerCard(controlledReplyForm.id, payload);
        toast.success("Controlled reply updated.");
      } else {
        await createControlledAnswerCard(payload);
        toast.success("Controlled reply created.");
      }
      setEditingControlledReply(null);
      setControlledReplyForm(EMPTY_CONTROLLED_REPLY_FORM);
      await loadControlledCards();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (controlledReplyForm.id
            ? "Failed to update controlled reply."
            : "Failed to create controlled reply.")
      );
    } finally {
      setSavingControlledReply(false);
    }
  };

  const saveReminderPolicy = async () => {
    if (reminderLoadedFlow !== activeFlowId || reminderConfigLoading || sectionErrors.reminders) { toast.error("Retry reminder configuration before saving."); return; }
    if (!activeFlow?.id) {
      toast.error("Select an active flow before saving reminder settings.");
      return;
    }

    const maxReminderCount = Number(reminderPolicyForm.maxReminderCount);
    if (Number.isNaN(maxReminderCount) || maxReminderCount < 0 || maxReminderCount > REMINDER_MAX_COUNT_LIMIT) {
      toast.error(`Max reminder count must be between 0 and ${REMINDER_MAX_COUNT_LIMIT}.`);
      return;
    }

    setSavingReminderPolicy(true);
    try {
      await updateLeadQualificationReminderPolicy(activeFlow.id, {
        isEnabled: Boolean(reminderPolicyForm.isEnabled),
        maxReminderCount,
        respectBusinessHours: Boolean(reminderPolicyForm.respectBusinessHours),
        timezone: reminderPolicyForm.timezone || null,
      });
      toast.success("Reminder policy saved.");
      await loadReminderConfig(activeFlow.id);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save reminder policy."
      );
    } finally {
      setSavingReminderPolicy(false);
    }
  };

  const buildDelayedResponseRequest = () => {
    if (!DELAYED_RESPONSE_PAYLOAD_TYPES.includes(delayedResponsePolicyForm.payloadType)) {
      toast.error("Choose a supported delayed-response payload. Custom captions are unavailable."); return null;
    }
    if (!activeFlow?.id) {
      toast.error("Select an active flow before saving delayed response policy.");
      return null;
    }
    const name = String(delayedResponsePolicyForm.name || "").trim();
    if (!name) {
      toast.error("Delayed response policy name is required.");
      return null;
    }
    if (
      delayedResponsePolicyForm.triggerEvent === "FieldCollected" &&
      !String(delayedResponsePolicyForm.triggerFieldKey || "").trim()
    ) {
      toast.error("FieldCollected trigger requires a field key.");
      return null;
    }
    if (
      DELAYED_RESPONSE_TEXT_PAYLOAD_TYPES.has(delayedResponsePolicyForm.payloadType) &&
      !String(delayedResponsePolicyForm.textTemplate || "").trim()
    ) {
      toast.error("Text payload requires a text template.");
      return null;
    }
    if (
      DELAYED_RESPONSE_MEDIA_PAYLOAD_TYPES.has(delayedResponsePolicyForm.payloadType) &&
      !String(delayedResponsePolicyForm.mediaAssetId || "").trim()
    ) {
      toast.error("Media payload requires a media asset id.");
      return null;
    }

    return buildDelayedResponsePolicyPayload(
      delayedResponsePolicyForm,
      activeFlow.id
    );
  };

  const saveDelayedResponsePolicy = async () => {
    const payload = buildDelayedResponseRequest();
    if (!payload) return;

    setSavingDelayedResponsePolicy(true);
    try {
      if (delayedResponsePolicyForm.id) {
        await updateDelayedResponsePolicy(delayedResponsePolicyForm.id, {
          ...payload,
          expectedPolicyRevision:
            Number(delayedResponsePolicyForm.policyRevision) || 1,
        });
        toast.success("Delayed response policy updated.");
      } else {
        await createDelayedResponsePolicy(payload);
        toast.success("Delayed response policy created.");
      }
      setDelayedResponsePolicyForm(EMPTY_DELAYED_RESPONSE_POLICY_FORM);
      setDelayedResponsePreview(null);
      await loadDelayedResponses();
    } catch (error) {
      const code = error?.response?.data?.code;
      const status = error?.response?.status;
      toast.error(
        status === 409 || code === "DelayedResponsePolicyRevisionConflict"
          ? "This policy was changed elsewhere. Refresh and try again."
          : error?.response?.data?.message ||
              "Failed to save delayed response policy."
      );
    } finally {
      setSavingDelayedResponsePolicy(false);
    }
  };

  const previewDelayedResponse = async () => {
    const current = request("delayedPreview");
    const payload = buildDelayedResponseRequest();
    if (!payload) return;

    setDelayedResponsePreview(null);
    setDelayedResponsePreviewError("");
    setPreviewingDelayedResponsePolicy(true);
    try {
      const result = await previewDelayedResponsePolicy({
        ...payload,
        sampleCustomerName: delayedResponseSampleValues.customerName || null,
        sampleBusinessName: delayedResponseSampleValues.businessName || null,
        sampleSummary: delayedResponseSampleValues.summary || null,
        sampleFields: delayedResponseSampleValues.fields || {},
      });
      if (current()) setDelayedResponsePreview(result || null);
    } catch (error) {
      if (!current()) return;
      toast.error(
        error?.response?.data?.message ||
          "Failed to preview delayed response policy."
      );
      setDelayedResponsePreviewError(error?.response?.data?.message || "Could not check this preview. Retry after reviewing your samples.");
    } finally {
      if (current()) setPreviewingDelayedResponsePolicy(false);
    }
  };

  const activateDelayedPolicy = async policy => {
    try {
      await activateDelayedResponsePolicy(policy.id);
      toast.success("Delayed response policy activated.");
      await loadDelayedResponses();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Delayed response policy could not be activated."
      );
    }
  };

  const deactivateDelayedPolicy = async policy => {
    try {
      await deactivateDelayedResponsePolicy(policy.id);
      toast.success("Delayed response policy deactivated.");
      await loadDelayedResponses();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Delayed response policy could not be deactivated."
      );
    }
  };

  const saveReminderStep = async () => {
    if (reminderLoadedFlow !== activeFlowId || reminderConfigLoading || sectionErrors.reminders) { toast.error("Retry reminder configuration before saving."); return; }
    if (reminderStepForm.useTemplateOutsideWindow) {
      toast.error("Outside-window template delivery is unavailable. Clear the older setting before saving."); return;
    }
    if (normalizeReminderMediaType(reminderMediaAssetLookup.get(reminderStepForm.optionalMediaAssetId)?.mediaType) === "Audio" && String(reminderStepForm.optionalMediaCaption || "").trim()) {
      toast.error("Audio captions are unavailable. Clear the caption before saving."); return;
    }
    if (!activeFlow?.id) {
      toast.error("Select an active flow before saving reminder steps.");
      return;
    }

    const sequence = Number(reminderStepForm.sequence);
    const delayMinutes = Number(reminderStepForm.delayMinutes);
    const messageText = String(reminderStepForm.messageText || "").trim();

    if (!Number.isFinite(sequence) || sequence <= 0) {
      toast.error("Sequence must be greater than zero.");
      return;
    }
    if (!Number.isFinite(delayMinutes) || delayMinutes <= 0) {
      toast.error("Delay minutes must be greater than zero.");
      return;
    }
    if (!messageText) {
      toast.error("Message text is required.");
      return;
    }

    const payload = {
      sequence,
      delayMinutes,
      messageText,
      includePendingQuestion: Boolean(reminderStepForm.includePendingQuestion),
      isActive: Boolean(reminderStepForm.isActive),
      optionalMediaAssetId: reminderStepForm.optionalMediaAssetId || null,
      optionalMediaCaption: reminderStepForm.optionalMediaCaption || null,
      useTemplateOutsideWindow: Boolean(reminderStepForm.useTemplateOutsideWindow),
      whatsAppTemplateName: reminderStepForm.whatsAppTemplateName || null,
      templateLanguage: reminderStepForm.templateLanguage || null,
      templateVariablesJson: reminderStepForm.templateVariablesJson || null,
    };

    setSavingReminderStep(true);
    try {
      if (reminderStepForm.id) {
        await updateLeadQualificationReminderStep(activeFlow.id, reminderStepForm.id, payload);
        toast.success("Reminder step updated.");
      } else {
        await createLeadQualificationReminderStep(activeFlow.id, payload);
        toast.success("Reminder step created.");
      }
      setEditingReminderStep(null);
      setReminderStepForm(EMPTY_REMINDER_STEP_FORM);
      setSelectedReminderMediaAsset(null);
      await loadReminderConfig(activeFlow.id);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (reminderStepForm.id
            ? "Failed to update reminder step."
            : "Failed to create reminder step.")
      );
    } finally {
      setSavingReminderStep(false);
    }
  };

  const toggleReminderStepActive = async step => {
    if (!activeFlow?.id) return;
    try {
      if (step.isActive) {
        await deactivateLeadQualificationReminderStep(activeFlow.id, step.id);
        toast.success("Reminder step deactivated.");
      } else {
        await activateLeadQualificationReminderStep(activeFlow.id, step.id);
        toast.success("Reminder step activated.");
      }
      await loadReminderConfig(activeFlow.id);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (step.isActive
            ? "Failed to deactivate reminder step."
            : "Failed to activate reminder step.")
      );
    }
  };

  const setControlledReplyActive = async (card, isActive) => {
    try {
      if (isActive) {
        await activateControlledAnswerCard(card.id);
        toast.success("Controlled reply activated.");
      } else {
        await deactivateControlledAnswerCard(card.id);
        toast.success("Controlled reply deactivated.");
      }
      await loadControlledCards();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (isActive
            ? "Failed to activate controlled reply."
            : "Failed to deactivate controlled reply.")
      );
    }
  };

  const saveField = async () => {
    if (!editingField || !canManage || savingField) return;
    setFieldError("");
    setSavingField(true);
    const payload = {
      displayName: fieldForm.displayName,
      questionText: fieldForm.questionText,
      resumeQuestionText: fieldForm.resumeQuestionText || null,
      isRequired: Boolean(fieldForm.isRequired),
      sortOrder: Number(fieldForm.sortOrder) || 0,
      validationType: fieldForm.validationType,
      confirmationPolicy: fieldForm.confirmationPolicy || "Never",
      extractionHintsJson: hintsToJson(editingField?.extractionHintsJson, valueAliasRows),
      isActive: Boolean(fieldForm.isActive),
    };
    try {
      if (editingField.id) {
        await updateAiLeadFlowField(editingField.id, payload);
        toast.success("Lead field updated.");
      } else {
        await createAiLeadFlowField({ ...payload, fieldKey: fieldForm.fieldKey });
        toast.success("Lead field created.");
      }
      setEditingField(null);
      await load({ silent: true });
    } catch (error) {
      setFieldError(error?.response?.data?.message || "Failed to save lead field.");
      toast.error(
        error?.response?.data?.message ||
          (editingField.id ? "Failed to update lead field." : "Failed to create lead field.")
      );
    } finally {
      setSavingField(false);
    }
  };

  const buildVersion2CheckpointPayload = React.useCallback(() => ({
    name: checkpointForm.name || "",
    policyVersion: 2,
    policyRevision: Number(checkpointForm.policyRevision) || 1,
    placementMode: checkpointForm.placementMode || "AfterFieldBeforeField",
    triggerAfterFieldKey: checkpointForm.triggerAfterFieldKey || null,
    beforeFieldKey: checkpointForm.beforeFieldKey || null,
    includedFieldKeys: checkpointForm.includedFieldKeys || [],
    summaryTemplate: checkpointForm.summaryTemplate || "",
    confirmationQuestionText: checkpointForm.confirmationQuestionText || "",
    allowCorrection: Boolean(checkpointForm.allowCorrection),
    correctionPromptText: checkpointForm.allowCorrection
      ? checkpointForm.correctionPromptText || ""
      : null,
    yesAction: checkpointForm.yesAction || "ContinueToNextField",
    yesResponseText: checkpointForm.yesResponseText || null,
    yesActionFieldKey: checkpointForm.yesAction === "AskConfiguredField"
      ? checkpointForm.yesActionFieldKey || null
      : null,
    noAction: checkpointForm.noAction || "Handover",
    noResponseText: checkpointForm.noResponseText || null,
    noActionFieldKey: checkpointForm.noAction === "AskConfiguredField"
      ? checkpointForm.noActionFieldKey || null
      : null,
    isActive: false,
    sortOrder: Number(checkpointForm.sortOrder) || 1,
    expectedPolicyRevision: checkpointForm.id
      ? Number(checkpointForm.policyRevision) || 1
      : null,
  }), [checkpointForm]);

  const previewCheckpointV2 = async () => {
    if (!activeFlowId) return;
    const current = request("checkpointPreview");
    setPreviewingCheckpoint(true);
    setCheckpointPreview(null);
    try {
      const result = await previewLeadQualificationConfirmationCheckpointV2(
        activeFlowId,
        {
          policy: buildVersion2CheckpointPayload(),
          sampleFieldValues: checkpointSampleValues,
        }
      );
      if (current()) setCheckpointPreview(result || null);
    } catch (error) {
      if (!current()) return;
      toast.error(
        error?.response?.data?.message ||
          "Failed to preview Version 2 summary checkpoint."
      );
    } finally {
      if (current()) setPreviewingCheckpoint(false);
    }
  };

  const saveCheckpoint = async () => {
    if (!activeFlowId) return;
    if (checkpointForm.policyVersion === 2) {
      if (!Array.isArray(checkpointForm.includedFieldKeys) || checkpointForm.includedFieldKeys.length === 0) {
        toast.error("Select at least one included field.");
        return;
      }
      setSavingCheckpoint(true);
      const payload = buildVersion2CheckpointPayload();
      try {
        if (checkpointForm.convertSourceCheckpointId) {
          await convertLeadQualificationConfirmationCheckpointToV2(
            activeFlowId,
            checkpointForm.convertSourceCheckpointId,
            {
              policy: payload,
              useLegacyIncludedFieldsWhenEmpty: false,
              useLegacySortOrder: false,
            }
          );
          toast.success("Version 2 draft created from legacy checkpoint.");
        } else if (checkpointForm.id) {
          await updateLeadQualificationConfirmationCheckpointV2(
            activeFlowId,
            checkpointForm.id,
            payload
          );
          toast.success("Version 2 summary checkpoint draft updated.");
        } else {
          await createLeadQualificationConfirmationCheckpointV2(activeFlowId, payload);
          toast.success("Version 2 summary checkpoint draft created.");
        }
        openCheckpointCreator();
        await load({ silent: true });
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to save Version 2 summary checkpoint."
        );
      } finally {
        setSavingCheckpoint(false);
      }
      return;
    }

    if (!checkpointForm.triggerAfterFieldKey || !checkpointForm.beforeFieldKey) {
      toast.error("Select trigger and before fields.");
      return;
    }
    if (!Array.isArray(checkpointForm.includedFieldKeys) || checkpointForm.includedFieldKeys.length === 0) {
      toast.error("Select at least one included field.");
      return;
    }

    setSavingCheckpoint(true);
    const payload = {
      triggerAfterFieldKey: checkpointForm.triggerAfterFieldKey,
      beforeFieldKey: checkpointForm.beforeFieldKey,
      includedFieldKeysJson: JSON.stringify(checkpointForm.includedFieldKeys),
      messageTemplate: checkpointForm.messageTemplate || DEFAULT_SUMMARY_CHECKPOINT_MESSAGE,
      confirmationRequired: Boolean(checkpointForm.confirmationRequired),
      allowEdit: Boolean(checkpointForm.allowEdit),
      isActive: Boolean(checkpointForm.isActive),
      sortOrder: Number(checkpointForm.sortOrder) || 1,
    };

    try {
      if (checkpointForm.id) {
        await updateLeadQualificationConfirmationCheckpoint(
          activeFlowId,
          checkpointForm.id,
          payload
        );
        toast.success("Summary checkpoint updated.");
      } else {
        await createLeadQualificationConfirmationCheckpoint(activeFlowId, payload);
        toast.success("Summary checkpoint created.");
      }
      openCheckpointCreator();
      await load({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (checkpointForm.id
            ? "Failed to update summary checkpoint."
            : "Failed to create summary checkpoint.")
      );
    } finally {
      setSavingCheckpoint(false);
    }
  };

  const activateCheckpoint = async checkpoint => {
    if (!activeFlowId || !checkpoint?.id) return;
    try {
      await activateLeadQualificationConfirmationCheckpoint(activeFlowId, checkpoint.id);
      toast.success("Summary checkpoint activated.");
      await load({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to activate summary checkpoint."
      );
    }
  };

  const deactivateCheckpoint = async checkpoint => {
    if (!activeFlowId || !checkpoint?.id) return;
    try {
      await deactivateLeadQualificationConfirmationCheckpoint(activeFlowId, checkpoint.id);
      toast.success("Summary checkpoint deactivated.");
      await load({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to deactivate summary checkpoint."
      );
    }
  };

  const saveRule = async () => {
    if (!editingRule) return;
    setSavingRule(true);
    const payload = {
      triggerName: ruleForm.triggerName,
      phoneNumberId: ruleForm.phoneNumberId || null,
      triggerType: ruleForm.triggerType,
      triggerPurpose: ruleForm.triggerPurpose,
      repeatPolicy: ruleForm.repeatPolicy,
      intent: ruleForm.intent || null,
      step: ruleForm.step || null,
      eventType: ruleForm.eventType || null,
      requiredFieldsJson: ruleForm.requiredFieldsJson || null,
      mediaCategory: ruleForm.mediaCategory,
      useCase: ruleForm.useCase || null,
      mediaType: ruleForm.mediaType || null,
      maxAssets: Number(ruleForm.maxAssets) || 1,
      priority: Number(ruleForm.priority) || 0,
      requiresConsent: Boolean(ruleForm.requiresConsent),
      isActive: Boolean(ruleForm.isActive),
      delaySeconds: Number(ruleForm.delaySeconds) || 0,
    };
    try {
      if (editingRule.id) {
        await updateAiLeadMediaTriggerRule(editingRule.id, payload);
        toast.success("Media trigger rule updated.");
      } else {
        await createAiLeadMediaTriggerRule(payload);
        toast.success("Media trigger rule created.");
      }
      setEditingRule(null);
      await load({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (editingRule.id ? "Failed to update media trigger rule." : "Failed to create media trigger rule.")
      );
    } finally {
      setSavingRule(false);
    }
  };

  const settings = config?.settingsSummary || {};
  const initializeFlow = async () => {
    if (!canManage || setupInFlight.current || !config || loadError) return;
    setupInFlight.current = true;
    setInitializingFlow(true);
    setSetupError("");
    try {
      const result = await initializeAiLeadQualificationFlow(selectedExistingFlowId || null);
      setConfig(result);
      selectStep("questions");
      toast.success("Qualification flow is ready to edit. Live sending has not been enabled.");
    } catch (error) {
      setSetupError(error?.response?.data?.message || "Could not create the qualification flow. Retry safely.");
    } finally {
      setupInFlight.current = false;
      setInitializingFlow(false);
    }
  };
  const reactivateField = async field => {
    if (!canManage || savingField) return;
    setSavingField(true);
    setFieldError("");
    try {
      await updateAiLeadFlowField(field.id, {
        displayName: field.displayName, questionText: field.questionText, resumeQuestionText: field.resumeQuestionText,
        isRequired: field.isRequired, sortOrder: field.sortOrder, validationType: field.validationType,
        confirmationPolicy: field.confirmationPolicy || "Never", extractionHintsJson: field.extractionHintsJson, isActive: true,
      });
      await load({ silent: true });
      toast.success("Field reactivated with its existing identity.");
    } catch (error) {
      setFieldError(error?.response?.data?.message || "Could not reactivate this field.");
    } finally { setSavingField(false); }
  };
  const activeFlow = config?.activeFlow || null;
  const activeFlowId = activeFlow?.id || null;
  const fields = React.useMemo(
    () => (Array.isArray(config?.fields) ? config.fields : []),
    [config?.fields]
  );
  const activeFields = React.useMemo(
    () => fields.filter(field => field.isActive !== false),
    [fields]
  );
  const visibleFields = fields.filter(field => fieldFilter === "All" || (field.isActive !== false) === (fieldFilter === "Active"));
  const availableFlows = Array.isArray(config?.availableFlows) ? config.availableFlows : [];
  const fieldNameByKey = React.useMemo(() => {
    const map = new Map();
    fields.forEach(field => {
      map.set(field.fieldKey, field.displayName || field.fieldKey);
    });
    return map;
  }, [fields]);
  const confirmationCheckpoints = Array.isArray(config?.confirmationCheckpoints)
    ? config.confirmationCheckpoints
    : [];
  const controlledCardsForEditingField = editingField?.fieldKey
    ? controlledCards.filter(
        card =>
          String(card?.linkedFieldKey || "").trim() ===
          String(editingField.fieldKey || "").trim()
      )
    : [];
  const rules = Array.isArray(config?.mediaTriggerRules)
    ? config.mediaTriggerRules
    : [];
  const proofLocations = Array.isArray(config?.regionalProofLocations)
    ? config.regionalProofLocations
    : [];
  const reminderStepsList = Array.isArray(reminderSteps) ? reminderSteps : [];
  const reminderSectionLoaded = Boolean(reminderConfig && reminderLoadedFlow === activeFlowId);
  const reminderMediaAssetLookup = React.useMemo(() => {
    const map = new Map();
    reminderMediaAssets.forEach(asset => {
      map.set(asset.id, asset);
    });
    if (selectedReminderMediaAsset?.id) {
      map.set(selectedReminderMediaAsset.id, {
        ...(map.get(selectedReminderMediaAsset.id) || {}),
        ...selectedReminderMediaAsset,
      });
    }
    return map;
  }, [reminderMediaAssets, selectedReminderMediaAsset]);
  const delayedResponsePoliciesForFlow = React.useMemo(
    () =>
      delayedResponsePolicies.filter(policy =>
        activeFlowId
          ? String(policy?.flowDefinitionId || "") === String(activeFlowId)
          : true
      ),
    [activeFlowId, delayedResponsePolicies]
  );
  const delayedResponsePolicyById = React.useMemo(() => {
    const map = new Map();
    delayedResponsePolicies.forEach(policy => map.set(policy.id, policy));
    return map;
  }, [delayedResponsePolicies]);
  const delayedResponseJobsForFlow = React.useMemo(() => {
    const filtered = delayedResponseJobs.filter(job =>
      activeFlowId
        ? String(job?.flowDefinitionId || "") === String(activeFlowId)
        : true
    );
    return delayedResponseJobStatusFilter === "All"
      ? filtered
      : filtered.filter(job => job.status === delayedResponseJobStatusFilter);
  }, [activeFlowId, delayedResponseJobs, delayedResponseJobStatusFilter]);
  const delayedResponseMediaAssetLookup = reminderMediaAssetLookup;
  const delayedResponseMediaAssets = reminderMediaAssets.filter(asset => normalizeReminderMediaType(asset.mediaType) === "Image" && asset.consentConfirmed === true);

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-6">
        {Object.entries(sectionErrors).filter(([key, message]) => message && key !== "mediaDetail" && !(key === "cards" && editingField)).map(([key, message]) => <AiLoadError key={key} message={message}
          onRetry={key === "cards" ? loadControlledCards : key === "reminders" ? () => loadReminderConfig(activeFlowId) : loadDelayedResponses} label={`qualification ${key}`} />)}
        {!editingRule ? <AiLoadError message={senderError} onRetry={() => setSenderRetry(value => value + 1)} label="senders" /> : null}
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="py-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-slate-900">
                  Lead collection
                </h1>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Choose questions, confirm enquiry details and plan follow-ups.
              </p>
            </div>
            <button
              type="button"
              onClick={() => load({ silent: true })}
              aria-label="Refresh lead collection" title="Refresh lead collection"
              disabled={initializingFlow || refreshing}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-500">
            Loading lead qualification config...
          </div>
        ) : loadError ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-white p-5 text-sm text-red-800">
            {loadError} <button type="button" onClick={() => load()} className="ml-2 underline">Retry</button>
          </div>
        ) : (
          <>
            {!activeFlow && config ? <section aria-label="Qualification flow setup" className="rounded-lg border border-emerald-200 bg-white p-5 text-sm">
              <h2 className="font-semibold text-slate-900">Set up lead qualification</h2>
              <p className="mt-2 text-slate-600">Choose the questions your assistant asks so your team receives useful customer requirements. Review the questions, configure summary confirmation, then review knowledge and activation checks. Creating a flow does not enable live sending.</p>
              {availableFlows.length ? <label className="mt-3 block text-slate-700">Existing flow
                <select value={selectedExistingFlowId} onChange={event => setSelectedExistingFlowId(event.target.value)} disabled={!canManage || initializingFlow} className="ml-2 rounded border border-slate-300 p-2">
                  <option value="">Select a flow to reuse</option>
                  {availableFlows.map(flow => <option key={flow.id} value={flow.id}>{flow.name}{flow.isActive ? "" : " (inactive)"}</option>)}
                </select>
              </label> : null}
              {setupError ? <p role="alert" className="mt-2 text-red-700">{setupError}</p> : null}
              <button type="button" onClick={initializeFlow} disabled={!canManage || initializingFlow || (availableFlows.length > 0 && !selectedExistingFlowId)} className="mt-3 rounded bg-emerald-600 px-4 py-2 font-medium text-white disabled:opacity-50">
                {initializingFlow ? "Setting up..." : availableFlows.length ? "Use selected flow" : "Create Qualification Flow"}
              </button>
              {!canManage ? <p className="mt-2 text-slate-600">Ask a user with AI sending management permission to set up this flow.</p> : null}
            </section> : null}
            <AiQualificationWorkspace steps={STEPS} currentStep={currentStep} visitedIds={visitedStepIds} onSelect={selectStep}>
            {currentStepId === "overview" ? <>
            <Section
              sectionId="lead-qualification-settings-summary"
              title="Collection status"
            >
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 rounded-md bg-slate-50 p-4 md:grid-cols-3">
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">AI assistant</div>
                  <div className="mt-1">
                    <BoolBadge value={Boolean(settings.isEnabled)} trueLabel="On" falseLabel="Off" />
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">
                    Collection preview
                  </div>
                  <div className="mt-1">
                    <BoolBadge
                      value={Boolean(settings.leadQualificationDryRunEnabled)}
                    />
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">
                    Collection mode
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-700">
                    {settings.leadQualificationAutoReplyMode === "DryRunOnly" ? "Preview only" : settings.leadQualificationAutoReplyMode || "Not available"}
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">
                    Approved media sending
                  </div>
                  <div className="mt-1">
                    <BoolBadge value={Boolean(settings.approvedMediaAutoSendEnabled)} />
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">Fallback preview</div>
                  <div className="mt-1">
                    <BoolBadge value={Boolean(settings.fallbackDryRunEnabled)} />
                  </div>
                </div>
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">Video sending</div>
                  <div className="mt-1">
                    <BoolBadge
                      value={Boolean(settings.autoReplyRollout?.videoAutoSendEnabled)}
                      trueLabel="Enabled"
                      falseLabel="Disabled"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                <div className="font-medium text-slate-900">Handover Message</div>
                <div className="mt-1 whitespace-pre-wrap">
                  {settings.handoverMessage || "Not configured"}
                </div>
              </div>
            </Section>
            <Section
              sectionId="active-default-lead-flow"
              title="Active lead flow"
            >
              {activeFlow ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Flow
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-900">
                      {activeFlow.name}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Active
                    </div>
                    <div className="mt-1">
                      <BoolBadge value={Boolean(activeFlow.isActive)} />
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Default
                    </div>
                    <div className="mt-1">
                      <BoolBadge value={Boolean(activeFlow.isDefault)} />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  No default active lead flow is configured for this business.
                </p>
              )}
            </Section>
            </> : null}
            {currentStepId === "confirmation" ? (
            <Section
              sectionId="lead-summary-confirmation-checkpoints"
              title="Review collected details"
            >
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <Info aria-hidden="true" className="mr-1.5 inline h-3.5 w-3.5" />Configure when the bot should show collected lead details and ask the customer to confirm before continuing.
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-slate-600">How confirmation works</summary>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-500">
                      {SUMMARY_CHECKPOINT_HELP_ITEMS.map(item => (
                        <li key={item.title}>
                          <span className="font-medium text-slate-600">{item.title}:</span> {item.text}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>

                {!activeFlowId ? (
                  <p className="text-sm text-slate-500">Select an active lead flow to configure summary checkpoints.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <details className="text-xs text-slate-600">
                        <summary className="cursor-pointer">Compatibility options</summary>
                        <button type="button" onClick={openCheckpointCreator} disabled={!canManage} className="mt-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">New legacy checkpoint</button>
                      </details>
                      <button type="button" onClick={openCheckpointV2Creator} disabled={!canManage} className="inline-flex h-9 items-center gap-2 rounded-md border border-emerald-600 bg-white px-3 text-sm text-emerald-700 hover:bg-emerald-50">
                        <Plus aria-hidden="true" className="h-4 w-4" />Add confirmation
                      </button>
                    </div>

                    {checkpointEditorOpen ? <div role="region" aria-label="Confirmation editor" className="space-y-3">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <h3 className="font-medium text-slate-900">{checkpointForm.id ? "Edit confirmation" : "New confirmation"}</h3>
                        <button type="button" disabled={savingCheckpoint} onClick={() => setCheckpointEditorOpen(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-slate-600">Close editor</button>
                      </div>
                    <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2">
                      <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        Confirmation type: {checkpointForm.policyVersion === 2 ? "Configurable confirmation (draft)" : "Legacy compatibility confirmation"}
                      </div>
                      {checkpointForm.policyVersion === 2 ? (
                        <>
                          <TextInput label="Name" name="name" value={checkpointForm.name} onChange={onCheckpointChange} />
                          <SelectInput label="Placement mode" name="placementMode" value={checkpointForm.placementMode} onChange={onCheckpointChange} options={SUMMARY_PLACEMENT_MODES} />
                          {["AfterField", "AfterFieldBeforeField"].includes(checkpointForm.placementMode) ? (
                            <SelectInput label="Trigger After Field" name="triggerAfterFieldKey" value={checkpointForm.triggerAfterFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                          ) : null}
                          {["BeforeField", "AfterFieldBeforeField"].includes(checkpointForm.placementMode) ? (
                            <SelectInput label="Before Field" name="beforeFieldKey" value={checkpointForm.beforeFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                          ) : null}
                          {checkpointForm.placementMode === "EndOfFlow" ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                              EndOfFlow opens before lead completion or lock. A missing next field is not treated as EndOfFlow unless this mode is explicitly selected.
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <SelectInput label="Trigger After Field" name="triggerAfterFieldKey" value={checkpointForm.triggerAfterFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                          <SelectInput label="Before Field" name="beforeFieldKey" value={checkpointForm.beforeFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                        </>
                      )}
                      <div className="md:col-span-2">
                        <div className="mb-1 text-sm text-slate-700">Included Fields</div>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                          {activeFields.map(field => (
                            <label key={field.fieldKey} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                              <input type="checkbox" checked={checkpointForm.includedFieldKeys.includes(field.fieldKey)} onChange={() => toggleCheckpointIncludedField(field.fieldKey)} />
                              <span title={field.fieldKey}>{field.displayName || field.fieldKey}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {checkpointForm.policyVersion === 2 ? (
                        <>
                          <TextInput label="Summary Template" name="summaryTemplate" value={checkpointForm.summaryTemplate} onChange={onCheckpointChange} multiline rows={4} help="Use {{summary}} where the collected field list should appear." />
                          <TextInput label="Confirmation Question Text" name="confirmationQuestionText" value={checkpointForm.confirmationQuestionText} onChange={onCheckpointChange} multiline rows={3} />
                          <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
                            <input type="checkbox" checked={Boolean(checkpointForm.allowCorrection)} onChange={event => onCheckpointChange("allowCorrection", event.target.checked)} />
                            Allow correction
                          </label>
                          {checkpointForm.allowCorrection ? (
                            <TextInput label="Correction Prompt Text" name="correctionPromptText" value={checkpointForm.correctionPromptText} onChange={onCheckpointChange} multiline rows={2} />
                          ) : (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                              If customer says No, the bot will not edit saved details. Configure the response and next action below. Business FAQ conversation remains available.
                            </div>
                          )}
                          <TextInput label="Yes Response Text" name="yesResponseText" value={checkpointForm.yesResponseText} onChange={onCheckpointChange} multiline rows={2} />
                          <SelectInput label="Yes action" name="yesAction" value={checkpointForm.yesAction} onChange={onCheckpointChange} options={SUMMARY_YES_ACTIONS} />
                          {SUMMARY_ACTIONS_REQUIRING_FIELD.has(checkpointForm.yesAction) ? (
                            <SelectInput label="Yes action field" name="yesActionFieldKey" value={checkpointForm.yesActionFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                          ) : null}
                          {checkpointForm.yesAction === "Wait" ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">Wait sends the configured response and waits. It does not schedule a delayed follow-up.</div>
                          ) : null}
                          {checkpointForm.yesAction === "CompleteLead" ? (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">This completes/locks the lead. FAQ can still be answered, but lead data mutation remains blocked.</div>
                          ) : null}
                          <TextInput label="No Response Text" name="noResponseText" value={checkpointForm.noResponseText} onChange={onCheckpointChange} multiline rows={2} />
                          <SelectInput label="No action" name="noAction" value={checkpointForm.noAction} onChange={onCheckpointChange} options={SUMMARY_NO_ACTIONS.filter(action => checkpointForm.allowCorrection || action !== "AskForCorrection")} />
                          {SUMMARY_ACTIONS_REQUIRING_FIELD.has(checkpointForm.noAction) ? (
                            <SelectInput label="No action field" name="noActionFieldKey" value={checkpointForm.noActionFieldKey} onChange={onCheckpointChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                          ) : null}
                          {checkpointForm.noAction === "Wait" || checkpointForm.noAction === "KeepPending" ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">This action sends configured response text and waits. It is not a delayed report or scheduled follow-up.</div>
                          ) : null}
                        </>
                      ) : (
                        <TextInput label="Message Template" name="messageTemplate" value={checkpointForm.messageTemplate} onChange={onCheckpointChange} multiline rows={5} />
                      )}
                      <label className="block text-sm text-slate-700">Sort Order<input type="number" min="1" value={checkpointForm.sortOrder} onChange={event => onCheckpointChange("sortOrder", Number(event.target.value))} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50" /></label>
                      {checkpointForm.policyVersion === 2 ? null : (
                        <div className="flex flex-wrap gap-3 md:col-span-2">
                          {[["confirmationRequired", "Confirmation Required"], ["allowEdit", "Allow correction after No"], ["isActive", "Active"]].map(([name, label]) => (
                            <label key={name} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={Boolean(checkpointForm[name])} onChange={event => onCheckpointChange(name, event.target.checked)} />{label}</label>
                          ))}
                        </div>
                      )}
                      {checkpointForm.policyVersion === 2 ? (
                        <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="mb-2 text-sm font-medium text-slate-700">Preview sample values</div>
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                            {checkpointForm.includedFieldKeys.map(fieldKey => (
                              <label key={fieldKey} className="block text-xs text-slate-600">
                                Sample {fieldNameByKey.get(fieldKey) || fieldKey}
                                <input value={checkpointSampleValues[fieldKey] || ""} onChange={event => onCheckpointSampleChange(fieldKey, event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50" />
                              </label>
                            ))}
                          </div>
                          <div className="mt-3 flex justify-end">
                            <button type="button" onClick={previewCheckpointV2} disabled={previewingCheckpoint || !canManage} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50">
                              {previewingCheckpoint ? "Previewing..." : "Preview confirmation"}
                            </button>
                          </div>
                          {checkpointPreview ? (
                            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                                <div className="font-medium text-slate-900">Rendered summary body</div>
                                <div className="mt-1 whitespace-pre-wrap">{checkpointPreview.renderedSummaryBody || "No summary body rendered."}</div>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                                <div className="font-medium text-slate-900">Confirmation question</div>
                                <div className="mt-1 whitespace-pre-wrap">{checkpointPreview.confirmationQuestionText || "Not configured"}</div>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 md:col-span-2">
                                <div className="font-medium text-slate-900">Customer-facing preview</div>
                                <div className="mt-1 whitespace-pre-wrap">{checkpointPreview.combinedMessagePreview || "No combined preview."}</div>
                                <div className="mt-2 text-xs text-slate-500">Yes: {checkpointPreview.yesOutcomeExplanation}</div>
                                <div className="text-xs text-slate-500">No: {checkpointPreview.noOutcomeExplanation}</div>
                                <div className="text-xs text-slate-500">{checkpointPreview.correctionBehaviorExplanation}</div>
                                <div className="text-xs text-slate-500">{checkpointPreview.postSummaryConversationBehaviorExplanation}</div>
                              </div>
                              {[...(checkpointPreview.errors || []), ...(checkpointPreview.warnings || [])].map(item => (
                                <div key={`${item.code}-${item.message}`} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 md:col-span-2">
                                  {item.code}: {item.message}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="flex justify-end gap-2 md:col-span-2">
                        {checkpointForm.id ? <button type="button" onClick={openCheckpointCreator} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel edit</button> : null}
                        <button type="button" onClick={saveCheckpoint} disabled={savingCheckpoint || !canManage} className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"><Save className="h-4 w-4" />{savingCheckpoint ? "Saving..." : checkpointForm.policyVersion === 2 ? (checkpointForm.id ? "Update confirmation draft" : checkpointForm.convertSourceCheckpointId ? "Save converted draft" : "Save confirmation draft") : checkpointForm.id ? "Update checkpoint" : "Create checkpoint"}</button>
                      </div>
                    </div>
                    </div> : null}

                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left font-medium text-slate-600">Policy</th><th className="px-3 py-2 text-left font-medium text-slate-600">Placement</th><th className="px-3 py-2 text-left font-medium text-slate-600">Included fields</th><th className="px-3 py-2 text-left font-medium text-slate-600">Effective outcomes</th><th className="px-3 py-2 text-left font-medium text-slate-600">Active</th><th className="px-3 py-2 text-left font-medium text-slate-600">Sort</th><th className="px-3 py-2 text-right font-medium text-slate-600">Action</th></tr></thead>
                      <tbody className="divide-y divide-slate-200">
                        {confirmationCheckpoints.map(checkpoint => {
                          const includedKeys = parseFieldKeysJson(checkpoint.includedFieldKeysJson);
                          const effective = summaryCheckpointEffectivePolicy(checkpoint);
                          const isLegacy = effective.policyVersion === 1;
                          return (
                            <tr key={checkpoint.id}>
                              <td className="max-w-xs px-3 py-3 align-top text-slate-700">
                                <div className="font-medium">{checkpoint.name || `Checkpoint ${checkpoint.sortOrder}`}</div>
                                <div className="mt-1 text-xs text-slate-500">Policy version: {isLegacy ? "Legacy v1" : `v${effective.policyVersion}`}</div>
                                <details className="mt-2 text-xs text-slate-600">
                                  <summary className="cursor-pointer"><Info aria-hidden="true" className="mr-1 inline h-3 w-3" />Policy details</summary>
                                {isLegacy ? (
                                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                                    {LEGACY_SUMMARY_CHECKPOINT_WARNING}
                                  </div>
                                ) : null}
                                {effective.warnings.map(warning => (
                                  <div key={warning.code || warning.message} className="mt-1 text-xs text-slate-500">
                                    {warning.message}
                                  </div>
                                ))}
                                  <div className="mt-2 text-xs text-slate-500">Execution: {effective.executionStatus}</div>
                                  <div className="text-xs text-slate-500">V2 editing/activation/execution: {effective.capabilities.v2EditingAvailable || effective.capabilities.v2ActivationAvailable || effective.capabilities.v2ExecutionAvailable ? "Available" : "Unavailable"}</div>
                                </details>
                              </td>
                              <td className="px-3 py-3 align-top text-slate-700">
                                <div className="font-medium">{OPTION_LABELS[effective.placementMode] || effective.placementMode}</div>
                                <div className="mt-1 text-xs text-slate-500">
                                  After: {fieldNameByKey.get(checkpoint.triggerAfterFieldKey) || checkpoint.triggerAfterFieldKey || "None"}
                                </div>
                                <div className="text-xs text-slate-500">
                                  Before: {fieldNameByKey.get(checkpoint.beforeFieldKey) || checkpoint.beforeFieldKey || "None"}
                                </div>
                              </td>
                              <td className="px-3 py-3 align-top text-slate-700"><div>{includedKeys.length} field{includedKeys.length === 1 ? "" : "s"}</div><div className="text-xs text-slate-500">{includedKeys.map(key => fieldNameByKey.get(key) || key).join(", ") || "None"}</div></td>
                              <td className="max-w-xs px-3 py-3 align-top text-slate-700">
                                <div><span className="font-medium">Yes:</span> {summaryCheckpointOutcomeLabel(effective.yesAction)}</div>
                                <div className="mt-1"><span className="font-medium">No:</span> {summaryCheckpointOutcomeLabel(effective.noAction)}</div>
                              </td>
                              <td className="px-3 py-3 align-top"><BoolBadge value={Boolean(checkpoint.isActive)} trueLabel="Active" falseLabel="Inactive" /></td>
                              <td className="px-3 py-3 align-top text-slate-700">{checkpoint.sortOrder}</td>
                              <td className="px-3 py-3 text-right align-top">
                                <div className="flex flex-wrap justify-end gap-1.5">
                                  <button type="button" onClick={() => openCheckpointEditor(checkpoint)} disabled={!canManage} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"><Pencil className="h-4 w-4" />{isLegacy ? "Edit" : "Edit V2"}</button>
                                  {isLegacy ? (
                                    <button type="button" onClick={() => openCheckpointV2Conversion(checkpoint)} disabled={!canManage || !effective.capabilities.v2DraftPersistenceAvailable} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50">Convert to V2</button>
                                  ) : null}
                                  {checkpoint.isActive ? (
                                    <button type="button" onClick={() => deactivateCheckpoint(checkpoint)} disabled={!canManage} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">Deactivate</button>
                                  ) : (
                                    <button type="button" onClick={() => activateCheckpoint(checkpoint)} disabled={!canManage || (effective.policyVersion === 2 && !effective.capabilities.v2ActivationAvailable)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50">Activate</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {confirmationCheckpoints.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-500">No summary confirmation checkpoints configured.</td></tr> : null}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </div>
            </Section>
            ) : null}
            {currentStepId === "followups" ? <>
            <Section
              sectionId="incomplete-lead-reminders"
              title="Incomplete enquiry reminders"
              actions={
                activeFlowId && canManage ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={saveReminderPolicy}
                      disabled={savingReminderPolicy || reminderConfigLoading || !reminderSectionLoaded || Boolean(sectionErrors.reminders)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Save className="h-4 w-4" />
                      {savingReminderPolicy ? "Saving..." : "Save policy"}
                    </button>
                    <button
                      type="button"
                      onClick={openReminderStepCreator}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add reminder step
                    </button>
                  </div>
                ) : null
              }
            >
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  Send configured follow-up reminders when a lead starts the qualification flow but stops replying before completion.
                  <div className="mt-1 text-xs text-slate-500">
                    Text and eligible approved media are supported inside an open WhatsApp window. Reminder sending depends on worker configuration.
                  </div>
                  {reminderSectionLoaded ? (
                    <div className="mt-1 text-xs text-slate-500">
                      Reminder configuration loaded for this flow.
                    </div>
                  ) : null}
                </div>

                {!activeFlowId ? (
                  <p className="text-sm text-slate-500">
                    Select an active lead flow to configure reminders.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(reminderPolicyForm.isEnabled)}
                          onChange={event =>
                            onReminderPolicyChange("isEnabled", event.target.checked)
                          }
                          disabled={!canManage}
                        />
                        Enable reminders
                      </label>
                      <label className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                        <input type="checkbox" checked={Boolean(reminderPolicyForm.respectBusinessHours)} onChange={event => onReminderPolicyChange("respectBusinessHours", event.target.checked)} disabled={!canManage || !reminderPolicyForm.respectBusinessHours} /> Respect business hours (unavailable)
                        <span className="mt-1 block text-xs text-slate-500">Business-hours scheduling is unavailable. Clear an older enabled value before enabling reminders.</span>
                      </label>
                      <label className="block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        Max reminder count
                        <input
                          type="number"
                          min="0"
                          max={REMINDER_MAX_COUNT_LIMIT}
                          value={reminderPolicyForm.maxReminderCount}
                          onChange={event =>
                            onReminderPolicyChange(
                              "maxReminderCount",
                              event.target.value === "" ? "" : Number(event.target.value)
                            )
                          }
                          disabled={!canManage}
                          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                        />
                      </label>
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Reminders are scheduled by the backend. They are only sent when the reminder worker is explicitly enabled.
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            Reminder steps
                          </h3>
                          <p className="text-xs text-slate-500">
                            Configure the reminder text and delay for each follow-up. Sequence order is unique within the flow.
                          </p>
                        </div>
                        {reminderStepsList.length > 0 ? (
                          <div className="text-xs text-slate-500">
                            {reminderStepsList.length} configured step{reminderStepsList.length === 1 ? "" : "s"}
                          </div>
                        ) : null}
                      </div>

                      {reminderStepsList.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                          {reminderConfigLoading ? "Loading reminder steps..." : sectionErrors.reminders ? "Reminder steps are unavailable. Retry the load." : "No reminder steps configured yet."}
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                          <table className="min-w-full divide-y divide-slate-200 text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Sequence</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Delay</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Message preview</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Media</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Pending question</th>
                                <th className="px-3 py-2 text-left font-medium text-slate-600">Active</th>
                                <th className="px-3 py-2 text-right font-medium text-slate-600">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {reminderStepsList.map(step => (
                                <tr key={step.id}>
                                  <td className="px-3 py-3 align-top text-slate-700">
                                    {step.sequence}
                                  </td>
                                  <td className="px-3 py-3 align-top text-slate-700">
                                    {step.delayMinutes} min
                                  </td>
                                  <td className="px-3 py-3 align-top text-slate-700 max-w-[360px]">
                                    <div className="truncate" title={step.messageText}>
                                      {shortPreview(step.messageText, 160) || "No message"}
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 align-top text-slate-700 max-w-[260px]">
                                    {step.optionalMediaAssetId ? (
                                      (() => {
                                        const asset = reminderMediaAssetLookup.get(step.optionalMediaAssetId);
                                        const mediaType = normalizeReminderMediaType(asset?.mediaType);
                                        const assetLabel = asset?.title || asset?.fileName || "Media configured";
                                        return (
                                          <div className="space-y-0.5">
                                            <div className="font-medium text-slate-900">
                                              {mediaType ? `${mediaType}: ${assetLabel}` : assetLabel}
                                            </div>
                                            {step.optionalMediaCaption ? (
                                              <div className="truncate text-xs text-slate-500" title={step.optionalMediaCaption}>
                                                Caption: {shortPreview(step.optionalMediaCaption, 60)}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })()
                                    ) : (
                                      <span className="text-xs text-slate-500">No media</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    <BoolBadge
                                      value={Boolean(step.includePendingQuestion)}
                                      trueLabel="Included"
                                      falseLabel="Not included"
                                    />
                                  </td>
                                  <td className="px-3 py-3 align-top">
                                    <BoolBadge value={Boolean(step.isActive)} />
                                  </td>
                                  <td className="px-3 py-3 text-right align-top">
                                    <div className="inline-flex flex-wrap gap-1.5">
                                      <button
                                        type="button"
                                        onClick={() => openReminderStepEditor(step)}
                                        disabled={!canManage}
                                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        <Pencil className="h-4 w-4" />
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => toggleReminderStepActive(step)}
                                        disabled={!canManage}
                                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        {step.isActive ? "Deactivate" : "Activate"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Section>
              <details className="rounded-md border border-slate-200 bg-slate-50 p-4">
                <summary className="cursor-pointer text-sm font-medium text-slate-700"><span>Event-based replies</span><span className="ml-2 text-xs font-normal text-slate-500">Advanced</span></summary>
                <p className="mt-2 text-xs text-slate-600">Send configured content after a supported flow event. These policies run separately from incomplete-enquiry reminders.</p>
                <div className="mt-5 space-y-6">
            <Section
              sectionId="delayed-response-policies"
              title="Event reply policies"
              actions={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={loadDelayedResponses}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <RefreshCcw className={`h-4 w-4 ${loadingDelayedResponses ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={openDelayedResponsePolicyCreator}
                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="h-4 w-4" />
                    Add event reply
                  </button>
                </div>
              }
            >
              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  Delayed responses send configured content after business events. Lead reminders are separate and only handle silent leads after the bot asked a question.
                </div>

                {delayedEditorOpen ? <div role="region" aria-label="Event reply editor" className="space-y-3">
                  <div className="flex justify-end"><button type="button" onClick={() => setDelayedEditorOpen(false)} disabled={savingDelayedResponsePolicy} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600">Close editor</button></div>
                <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2">
                  <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {delayedResponsePolicyForm.id ? "Edit event reply" : "New event reply"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Activation is explicit. Saving a policy does not send or schedule delayed responses.
                      </div>
                    </div>
                    {delayedResponsePolicyForm.id ? (
                      <BoolBadge value={Boolean(delayedResponsePolicyForm.isActive)} trueLabel="Active" falseLabel="Inactive" />
                    ) : null}
                  </div>

                  <TextInput label="Name" name="name" value={delayedResponsePolicyForm.name} onChange={onDelayedResponsePolicyChange} />
                  <SelectInput label="Trigger event" name="triggerEvent" value={delayedResponsePolicyForm.triggerEvent} onChange={onDelayedResponsePolicyChange} options={DELAYED_RESPONSE_TRIGGER_EVENTS} />
                  {delayedResponsePolicyForm.triggerEvent === "FieldCollected" ? (
                    activeFields.length > 0 ? (
                      <SelectInput label="Trigger field key" name="triggerFieldKey" value={delayedResponsePolicyForm.triggerFieldKey} onChange={onDelayedResponsePolicyChange} options={activeFields.map(field => field.fieldKey)} optionLabels={Object.fromEntries(fieldNameByKey)} allowBlank />
                    ) : (
                      <TextInput label="Trigger field key" name="triggerFieldKey" value={delayedResponsePolicyForm.triggerFieldKey} onChange={onDelayedResponsePolicyChange} />
                    )
                  ) : null}
                  <label className="block text-sm text-slate-700">Summary checkpoint (optional)
                    <select value={delayedResponsePolicyForm.triggerCheckpointId} onChange={event => onDelayedResponsePolicyChange("triggerCheckpointId", event.target.value)} className="mt-1 block w-full rounded border p-2">
                      <option value="">Any matching checkpoint</option>
                      {delayedResponsePolicyForm.triggerCheckpointId && !confirmationCheckpoints.some(item => item.id === delayedResponsePolicyForm.triggerCheckpointId) ? <option value={delayedResponsePolicyForm.triggerCheckpointId}>Saved checkpoint (unavailable)</option> : null}
                      {confirmationCheckpoints.map(item => <option key={item.id} value={item.id}>{item.name || `Summary after ${item.triggerAfterFieldKey || "the flow"}`}{item.isActive ? "" : " (inactive)"}</option>)}
                    </select>
                  </label>
                  <TextInput label="Trigger action (optional)" name="triggerAction" value={delayedResponsePolicyForm.triggerAction} onChange={onDelayedResponsePolicyChange} />
                  <TextInput label="Delay seconds" name="delaySeconds" type="number" value={delayedResponsePolicyForm.delaySeconds} onChange={onDelayedResponsePolicyChange} />
                  <TextInput label="Sort order" name="sortOrder" type="number" value={delayedResponsePolicyForm.sortOrder} onChange={onDelayedResponsePolicyChange} />
                  <SelectInput label="Repeat policy" name="repeatPolicy" value={delayedResponsePolicyForm.repeatPolicy} onChange={onDelayedResponsePolicyChange} options={DELAYED_RESPONSE_REPEAT_POLICIES} />
                  <TextInput label="Max sends per session" name="maxSendsPerSession" type="number" value={delayedResponsePolicyForm.maxSendsPerSession} onChange={onDelayedResponsePolicyChange} />
                  <label className="block text-sm text-slate-700">Payload type
                    <select value={delayedResponsePolicyForm.payloadType} onChange={event => onDelayedResponsePolicyChange("payloadType", event.target.value)} className="mt-1 w-full rounded border border-slate-300 p-2">
                      {!DELAYED_RESPONSE_PAYLOAD_TYPES.includes(delayedResponsePolicyForm.payloadType) ? <option value={delayedResponsePolicyForm.payloadType} disabled>{delayedResponsePolicyForm.payloadType} (unavailable)</option> : null}
                      {DELAYED_RESPONSE_PAYLOAD_TYPES.map(type => <option key={type}>{type}</option>)}
                    </select>
                  </label>
                  {delayedResponsePolicyForm.payloadType === "MediaWithCaption" ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      MediaWithCaption remains fail-closed until an independent safe caption send path is available.
                    </div>
                  ) : null}
                  {DELAYED_RESPONSE_TEXT_PAYLOAD_TYPES.has(delayedResponsePolicyForm.payloadType) ? (
                    <label className="md:col-span-2 text-sm">
                      <span className="mb-1 block font-medium text-slate-700">Text template</span>
                      <textarea
                        value={delayedResponsePolicyForm.textTemplate}
                        onChange={event => onDelayedResponsePolicyChange("textTemplate", event.target.value)}
                        rows={4}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                      />
                    </label>
                  ) : null}
                  {DELAYED_RESPONSE_MEDIA_PAYLOAD_TYPES.has(delayedResponsePolicyForm.payloadType) ? (
                    <div className="space-y-2">
                      <details><summary className="text-xs text-slate-600">Advanced media identifier</summary><TextInput label="Media asset id" name="mediaAssetId" value={delayedResponsePolicyForm.mediaAssetId} onChange={onDelayedResponsePolicyChange} /></details>
                      {delayedResponseMediaAssets.length === 0 ? <p className="text-sm text-slate-600">No eligible image is available. Approve an image in Media Assets for the intended sender, then refresh this setup.</p> : null}
                      {delayedResponseMediaAssets.length > 0 ? (
                        <label className="text-sm">
                          <span className="mb-1 block font-medium text-slate-700">Choose approved media asset</span>
                          <select
                            value={delayedResponsePolicyForm.mediaAssetId}
                            onChange={event => onDelayedResponsePolicyChange("mediaAssetId", event.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          >
                            <option value="">Manual / not selected</option>
                            {delayedResponseMediaAssets.map(asset => (
                              <option key={asset.id} value={asset.id}>
                                {asset.fileName || asset.displayName || asset.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <p className="text-xs text-slate-500">No approved media asset selector data is loaded. Enter the media asset id manually.</p>
                      )}
                      {delayedResponsePolicyForm.mediaAssetId ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                          Media asset: {delayedResponseMediaAssetLookup.get(delayedResponsePolicyForm.mediaAssetId)?.fileName || delayedResponsePolicyForm.mediaAssetId}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    Supported placeholders: {"{{customerName}}"}, {"{{businessName}}"}, {"{{summary}}"}, and {"{{field.<field_key>}}"}.
                    <p className="mt-1">Delayed responses support separate text and approved images. Documents, video, audio and custom captions are unavailable here. Fill every referenced sample value to preview. Content is captured at the triggering event; delivery rechecks the current WhatsApp window and safety rules. A closed window blocks delivery.</p>
                  </div>
                  <details className="md:col-span-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <summary className="cursor-pointer font-medium text-slate-700">Advanced guard and cancellation JSON</summary>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="text-sm">
                        <span className="mb-1 block font-medium text-slate-700">Guard rules JSON</span>
                        <textarea
                          value={delayedResponsePolicyForm.guardRulesJson}
                          onChange={event => onDelayedResponsePolicyChange("guardRulesJson", event.target.value)}
                          rows={5}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          placeholder="Leave blank to use backend defaults"
                        />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1 block font-medium text-slate-700">Cancellation policy JSON</span>
                        <textarea
                          value={delayedResponsePolicyForm.cancellationPolicyJson}
                          onChange={event => onDelayedResponsePolicyChange("cancellationPolicyJson", event.target.value)}
                          rows={5}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                          placeholder="Leave blank to use backend defaults"
                        />
                      </label>
                    </div>
                  </details>

                  <div className="md:col-span-2 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-3">
                    <TextInput label="Sample customer name" name="customerName" value={delayedResponseSampleValues.customerName} onChange={onDelayedResponseSampleChange} />
                    <TextInput label="Sample business name" name="businessName" value={delayedResponseSampleValues.businessName} onChange={onDelayedResponseSampleChange} />
                    <TextInput label="Sample summary" name="summary" value={delayedResponseSampleValues.summary} onChange={onDelayedResponseSampleChange} />
                    {activeFields.map(field => (
                      <TextInput
                        key={field.fieldKey}
                        label={`Sample ${field.displayName || field.fieldKey}`}
                        name={field.fieldKey}
                        value={delayedResponseSampleValues.fields?.[field.fieldKey] || ""}
                        onChange={onDelayedResponseSampleFieldChange}
                      />
                    ))}
                  </div>

                  <div className="md:col-span-2 flex flex-wrap justify-end gap-2">
                    <button type="button" onClick={previewDelayedResponse} disabled={previewingDelayedResponsePolicy} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      <Eye className="h-4 w-4" />
                      {previewingDelayedResponsePolicy ? "Previewing..." : "Preview"}
                    </button>
                    <button type="button" onClick={saveDelayedResponsePolicy} disabled={savingDelayedResponsePolicy} className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                      <Save className="h-4 w-4" />
                      {savingDelayedResponsePolicy ? "Saving..." : delayedResponsePolicyForm.id ? "Update policy" : "Create policy"}
                    </button>
                  </div>

                  {delayedResponsePreviewError ? <p role="alert" className="md:col-span-2 text-sm text-red-700">{delayedResponsePreviewError}</p> : null}
                  {delayedResponsePreview ? (
                    <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-emerald-900">
                      <div className="font-medium">Preview</div>
                      <p role="status">{delayedResponsePreview.isValid === true ? "Preview checks passed. Delivery remains conditional." : "Preview blocked — review the issues below."}</p>
                      {Array.isArray(delayedResponsePreview.stages) ? <ol aria-label="Delivery stages" className="mt-2 list-decimal space-y-1 pl-5">
                        {delayedResponsePreview.stages.map((stage, index) => <li key={index}>{stage.kind === "Text" ? `Text: ${stage.text || "Missing sample content"}` : `Image: ${delayedResponseMediaAssetLookup.get(stage.mediaAssetId)?.title || stage.mediaAssetId || "Not selected"}`}</li>)}
                      </ol> : null}
                      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                        <div><span className="font-medium">Trigger:</span> {delayedResponsePreview.triggerSummary || "-"}</div>
                        <div><span className="font-medium">Estimated due:</span> {formatDateTime(delayedResponsePreview.estimatedDueAtUtc)}</div>
                        <div><span className="font-medium">Payload:</span> {delayedResponsePreview.messageOrder || delayedResponsePolicyForm.payloadType}</div>
                        <div><span className="font-medium">Media:</span> {delayedResponsePreview.mediaSelectionSummary || "-"}</div>
                      </div>
                      <div className="mt-2 whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-slate-800">
                        {delayedResponsePreview.renderedText || "No rendered text returned."}
                      </div>
                      {Array.isArray(delayedResponsePreview.warnings) && delayedResponsePreview.warnings.length > 0 ? (
                        <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">
                          {delayedResponsePreview.warnings.map((warning, index) => (
                            <li key={`${warning.code || "warning"}-${index}`}>{warning.message || warning.code || String(warning)}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                </div> : null}

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Active</th>
                        <th className="px-3 py-2">Trigger</th>
                        <th className="px-3 py-2">Delay</th>
                        <th className="px-3 py-2">Payload</th>
                        <th className="px-3 py-2">Repeat</th>
                        <th className="px-3 py-2">Revision</th>
                        <th className="px-3 py-2">Updated</th>
                        <th className="px-3 py-2">Warnings</th>
                        <th className="px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {delayedResponsePoliciesForFlow.length === 0 ? (
                        <tr><td colSpan={10} className="px-3 py-4 text-center text-slate-500">{loadingDelayedResponses ? "Loading delayed policies..." : sectionErrors.delayed ? "Delayed policies are unavailable. Retry the load." : "No delayed response policies configured."}</td></tr>
                      ) : (
                        delayedResponsePoliciesForFlow.map(policy => {
                          const payload = delayedResponsePayloadFromPolicy(policy);
                          return (
                            <tr key={policy.id}>
                              <td className="px-3 py-2 font-medium text-slate-900">{policy.name}</td>
                              <td className="px-3 py-2"><BoolBadge value={Boolean(policy.isActive)} trueLabel="Active" falseLabel="Inactive" /></td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{policy.triggerEvent}</div>
                                {policy.triggerFieldKey ? <div className="text-xs text-slate-500">Field: {policy.triggerFieldKey}</div> : null}
                                {policy.triggerCheckpointId ? <div className="text-xs text-slate-500">Checkpoint: {policy.triggerCheckpointId}</div> : null}
                                {policy.triggerAction ? <div className="text-xs text-slate-500">Action: {policy.triggerAction}</div> : null}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{policy.delaySeconds}s</td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{payload.payloadType || "-"}</div>
                                {payload.mediaAssetId ? <div className="text-xs text-slate-500">Media: {payload.mediaAssetId}</div> : null}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{policy.repeatPolicy}</div>
                                <div className="text-xs text-slate-500">Max {policy.maxSendsPerSession}</div>
                              </td>
                              <td className="px-3 py-2 text-slate-700">{policy.policyRevision}</td>
                              <td className="px-3 py-2 text-slate-700">{formatDateTime(policy.updatedAtUtc)}</td>
                              <td className="px-3 py-2 text-xs text-amber-700">
                                {Array.isArray(policy.warnings) && policy.warnings.length > 0
                                  ? policy.warnings.map(warning => warning.message || warning.code || String(warning)).join("; ")
                                  : "-"}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-2">
                                  <button type="button" onClick={() => openDelayedResponsePolicyEditor(policy)} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                                    <Pencil className="h-3.5 w-3.5" />Edit / Preview
                                  </button>
                                  {policy.isActive ? (
                                    <button type="button" onClick={() => deactivateDelayedPolicy(policy)} className="rounded border border-amber-200 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50">Deactivate</button>
                                  ) : (
                                    <button type="button" onClick={() => activateDelayedPolicy(policy)} className="rounded border border-emerald-200 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">Activate</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>
            <Section
              sectionId="scheduled-delayed-response-jobs"
              title="Recent scheduled work"
              actions={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <select
                    value={delayedResponseJobStatusFilter}
                    onChange={event => setDelayedResponseJobStatusFilter(event.target.value)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  >
                    {DELAYED_RESPONSE_JOB_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                  <button type="button" onClick={loadDelayedResponses} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <RefreshCcw className={`h-4 w-4 ${loadingDelayedResponses ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                </div>
              }
            >
              <div className="space-y-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  View replies scheduled by flow events, including delivery errors and sending history.
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Policy</th>
                        <th className="px-3 py-2">Trigger</th>
                        <th className="px-3 py-2">Due</th>
                        <th className="px-3 py-2">Attempts</th>
                        <th className="px-3 py-2">Last error</th>
                        <th className="px-3 py-2">Payload</th>
                        <th className="px-3 py-2">Sent logs</th>
                        <th className="px-3 py-2">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {delayedResponseJobsForFlow.length === 0 ? (
                        <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-500">{loadingDelayedResponses ? "Loading delayed jobs..." : sectionErrors.delayed ? "Delayed jobs are unavailable. Retry the load." : "No scheduled delayed response jobs found."}</td></tr>
                      ) : (
                        delayedResponseJobsForFlow.map(job => {
                          const policy = delayedResponsePolicyById.get(job.policyId);
                          const payload = parseJsonObject(job.payloadSnapshotJson, {});
                          const isMediaError = DELAYED_RESPONSE_MEDIA_ERROR_CODES.has(job.lastErrorCode);
                          return (
                            <tr key={job.id}>
                              <td className="px-3 py-2">
                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${job.status === "Failed" ? "bg-red-50 text-red-700" : job.status === "Sent" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                                  {job.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{policy?.name || job.policyId}</div>
                                <div className="text-xs text-slate-500">rev {job.policyRevision}</div>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{job.triggerEvent}</div>
                                {job.triggerFieldKey ? <div className="text-xs text-slate-500">Field: {job.triggerFieldKey}</div> : null}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{formatDateTime(job.dueAtUtc)}</td>
                              <td className="px-3 py-2 text-slate-700">{job.attemptCount || 0}/{job.maxAttempts || "-"}</td>
                              <td className="px-3 py-2 text-slate-700">
                                {job.lastErrorCode ? (
                                  <div className={isMediaError ? "rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700" : "text-xs text-slate-600"}>
                                    <div className="font-medium">{job.lastErrorCode}</div>
                                    {job.lastErrorMessage ? <div>{job.lastErrorMessage}</div> : null}
                                  </div>
                                ) : "-"}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">
                                {payload.payloadType || "-"}
                                {payload.mediaAssetId ? <div>Media: {payload.mediaAssetId}</div> : null}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600">{shortPreview(job.sentMessageLogIdsJson, 80) || "-"}</td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{formatDateTime(job.updatedAtUtc)}</div>
                                {job.completedAtUtc ? <div className="text-xs text-emerald-700">Completed {formatDateTime(job.completedAtUtc)}</div> : null}
                                {job.failedAtUtc ? <div className="text-xs text-red-700">Failed {formatDateTime(job.failedAtUtc)}</div> : null}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>
                </div>
              </details>
            </> : null}

            {currentStepId === "questions" ? (
            <Section
              sectionId="lead-flow-fields"
              title="Questions to collect"
              actions={
                canManage ? (
                  <button
                    type="button"
                    onClick={openFieldCreator}
                    disabled={!activeFlowId || initializingFlow}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="h-4 w-4" />
                    Add question
                  </button>
                ) : null
              }
            >
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                <label>Show questions <select aria-label="Show questions" value={fieldFilter} onChange={event => setFieldFilter(event.target.value)} className="ml-2 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-emerald-500 focus:outline-none">
                  {["Active", "Inactive", "All"].map(value => <option key={value}>{value}</option>)}
                </select></label>
                <details className="text-xs text-slate-600">
                  <summary className="cursor-pointer"><Info aria-hidden="true" className="mr-1 inline h-3.5 w-3.5" />About these questions</summary>
                  <p className="mt-2 max-w-md">Only active questions are asked. Inactive questions retain their collected answers. Use Edit for approved replies to questions or objections; business knowledge and fallback wording stay in Setup.</p>
                </details>
              </div>
              {fieldError ? <p role="alert" className="mb-3 text-sm text-red-700">{fieldError}</p> : null}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Field
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Question
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Validation
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Required
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Sort
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Active
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {visibleFields.map(field => (
                      <tr key={field.id}>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-slate-900">
                            {field.displayName}
                            {isConcernLikeField(field) && (
                              <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-xs font-normal text-amber-700">
                                concern-like
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {field.fieldKey}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          {field.questionText}
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          {field.validationType}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <BoolBadge value={Boolean(field.isRequired)} />
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          {field.sortOrder}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <BoolBadge value={Boolean(field.isActive)} />
                        </td>
                        <td className="px-3 py-3 text-right align-top">
                          <button
                            type="button"
                            onClick={() => openFieldEditor(field)}
                            disabled={!canManage}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                          {field.isActive === false ? <button type="button" onClick={() => reactivateField(field)} disabled={!canManage || savingField} className="ml-2 rounded border border-emerald-200 px-3 py-1.5 text-sm text-emerald-700 disabled:opacity-50">Reactivate</button> : null}
                        </td>
                      </tr>
                    ))}
                    {visibleFields.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-sm text-slate-500"
                        >
                          No {fieldFilter.toLowerCase()} lead flow fields found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Section>
            ) : null}

            {currentStepId === "media" ? (
            <Section
              sectionId="media-trigger-rules"
              title="Media rules"
              actions={
                canManage ? (
                  <button
                    type="button"
                    onClick={openRuleCreator}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="h-4 w-4" />
                    Add Rule
                  </button>
                ) : null
              }
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Rule
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Match
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Media
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Priority
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">
                        Active
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {rules.map(rule => (
                      <tr key={rule.id}>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-slate-900">
                            {rule.triggerName}
                          </div>
                          <div className="text-xs text-slate-500">
                            {rule.triggerPurpose} / {rule.repeatPolicy}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div>{rule.triggerType}</div>
                          <div className="text-xs text-slate-500">
                            {rule.intent || rule.step || "Required fields only"}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div>{rule.mediaCategory}</div>
                          <div className="text-xs text-slate-500">
                            {rule.useCase || "No use case"} / {rule.mediaType || "Any"}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          {rule.priority}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <BoolBadge value={Boolean(rule.isActive)} />
                        </td>
                        <td className="px-3 py-3 text-right align-top">
                          <button
                            type="button"
                            onClick={() => openRuleEditor(rule)}
                            disabled={!canManage}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rules.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-6 text-center text-sm text-slate-500"
                        >
                          No media trigger rules found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Section>
            ) : null}

            {currentStepId === "proof" ? (
            <Section
              sectionId="regional-proof-locations"
              title="Regional examples"
              actions={
                canManage ? (
                  <button
                    type="button"
                    onClick={openProofCreator}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="h-4 w-4" />
                    Add Location
                  </button>
                ) : null
              }
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Location</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Nearby Areas</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Template</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Active</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {proofLocations.map(location => (
                      <tr key={location.id}>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-slate-900">{location.locationName}</div>
                          <div className="text-xs text-slate-500">
                            {location.district || "No district"} / {location.state || "No state"}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700 max-w-[200px] truncate">
                          {location.nearbyAreasJson || "Not configured"}
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700 max-w-[220px] truncate">
                          {location.approvedWordingTemplate || "Not configured"}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <BoolBadge value={Boolean(location.isActive)} />
                        </td>
                        <td className="px-3 py-3 text-right align-top">
                          <div className="inline-flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => openProofEditor(location)}
                              disabled={!canManage}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </button>
                            {location.isActive ? (
                              <button
                                type="button"
                                onClick={() => deactivateProof(location)}
                                disabled={!canManage}
                                className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Deactivate
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {proofLocations.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-500">
                          No regional proof locations found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Section>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={goBack}
                disabled={isFirstStep}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              {!isLastStep ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            </AiQualificationWorkspace>
          </>
        )}
      </div>

      {editingField ? (
        <Modal
          title={editingField.id ? `Edit question: ${editingField.displayName}` : "Add question"}
          error={sectionErrors.cards} onRetry={loadControlledCards}
          onClose={() => setEditingField(null)}
        >
          {editingField.id ? <p className="mb-3 text-xs text-slate-600">Field key: {editingField.fieldKey}. The key and identity stay fixed to preserve existing sessions and references.</p> : null}
          {fieldError ? <p role="alert" className="mb-3 text-sm text-red-700">{fieldError}</p> : null}
          {editingField.isRequired && editingField.isActive !== false && !fieldForm.isActive ? <p className="mb-3 text-sm text-amber-800">This required question will no longer be asked or count as missing. Existing collected values are retained.</p> : null}
          {(editingField.deactivationBlockers || []).length ? <p className="mb-3 text-xs text-amber-800">Repair or deactivate these references before deactivating the field: {editingField.deactivationBlockers.join("; ")}</p> : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {!editingField.id ? (
              <label className="block text-sm text-slate-700 md:col-span-2">
                Field key
                <span className="ml-1 text-xs text-slate-400">
                  lowercase letters, digits and underscores; e.g. customer_city
                </span>
                <input
                  value={fieldForm.fieldKey}
                  onChange={event => onFieldChange("fieldKey", event.target.value)}
                  placeholder="e.g. customer_city"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                />
              </label>
            ) : null}
            <TextInput label="Display name" name="displayName" value={fieldForm.displayName} onChange={onFieldChange} />
            <label className="block text-sm text-slate-700">Sort order
              <input type="number" value={fieldForm.sortOrder} onChange={event => onFieldChange("sortOrder", Number(event.target.value))} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none" />
            </label>
            <div className="md:col-span-2"><TextInput label="Question text" help="The question your customer sees." name="questionText" value={fieldForm.questionText} onChange={onFieldChange} multiline rows={3} /></div>
            <SelectInput label="Validation type" name="validationType" value={fieldForm.validationType} onChange={onFieldChange}
              options={fieldForm.validationType && !KNOWN_VALIDATION_TYPES.includes(fieldForm.validationType) ? [...KNOWN_VALIDATION_TYPES, fieldForm.validationType] : KNOWN_VALIDATION_TYPES} allowBlank />
            <SelectInput label="Confirmation Policy" name="confirmationPolicy" value={fieldForm.confirmationPolicy || "Never"} onChange={onFieldChange} options={CONFIRMATION_POLICIES} />
            <div className="flex flex-wrap gap-5 md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={fieldForm.isRequired} onChange={event => onFieldChange("isRequired", event.target.checked)} />Required</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={fieldForm.isActive} disabled={editingField.isActive !== false && (editingField.deactivationBlockers || []).length > 0} onChange={event => onFieldChange("isActive", event.target.checked)} />Active</label>
            </div>
            <details className="rounded-md border border-slate-200 p-3 md:col-span-2">
              <summary className="cursor-pointer text-sm text-slate-700">Optional return wording</summary>
              <div className="mt-3"><TextInput label="Resume question text" help="Used when your assistant returns to this question after answering an interruption or objection." name="resumeQuestionText" value={fieldForm.resumeQuestionText} onChange={onFieldChange} multiline rows={3} /></div>
            </details>
            <details open={hintsParseWarning || undefined} className="rounded-md border border-slate-200 p-3 md:col-span-2">
              <summary className="cursor-pointer text-sm text-slate-700">Answer aliases (advanced)</summary>
              <div className="mt-3">
            <div className="md:col-span-2 space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-slate-700">Value Aliases</span>
                <span className="text-xs text-slate-400">
                  what user may say → how to save it
                </span>
              </div>
              {hintsParseWarning && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Existing hints JSON is invalid. Saving will replace it with valid JSON.
                </div>
              )}
              {valueAliasRows.length > 0 && (
                <div className="space-y-1.5">
                  {valueAliasRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        placeholder="User may say…"
                        value={row.key}
                        onChange={e => {
                          const next = [...valueAliasRows];
                          next[i] = { ...next[i], key: e.target.value };
                          setValueAliasRows(next);
                        }}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                      />
                      <span className="text-xs text-slate-400">→</span>
                      <input
                        placeholder="Save as…"
                        value={row.value}
                        onChange={e => {
                          const next = [...valueAliasRows];
                          next[i] = { ...next[i], value: e.target.value };
                          setValueAliasRows(next);
                        }}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setValueAliasRows(r => r.filter((_, j) => j !== i))}
                        className="px-1 text-slate-400 hover:text-red-500"
                        title="Remove row"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {valueAliasRows.length === 0 && (
                <p className="text-xs text-slate-400">No aliases configured yet.</p>
              )}
              <button
                type="button"
                onClick={() => setValueAliasRows(r => [...r, { key: "", value: "" }])}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                + Add alias
              </button>
              <div className="border-t border-slate-100 pt-2">
                <button
                  type="button"
                  onClick={() => setShowJsonPreview(p => !p)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  {showJsonPreview ? "▲ Hide" : "▼ Show"} merged JSON preview
                </button>
                {showJsonPreview && (
                  <pre className="mt-1 max-h-40 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    {hintsToJson(editingField?.extractionHintsJson, valueAliasRows) ?? "null (no hints will be stored)"}
                  </pre>
                )}
              </div>
            </div>
              </div>
            </details>
            <div className="md:col-span-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setShowControlledReplies(open => !open)}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <span>
                  <span className="block text-sm font-semibold text-slate-900">
                    Configured replies for this question
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Set approved wording for customer questions or objections at this step. Sending is controlled separately in AI Agent Settings.
                  </span>
                </span>
                <span className="text-xs font-medium text-blue-600">
                  {showControlledReplies ? "Hide" : "Show"}
                </span>
              </button>

              {showControlledReplies ? (
                <div className="mt-4 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  {!editingField.id ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Save this lead field before adding controlled replies.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs text-slate-500">
                          Linked field:{" "}
                          <span className="font-mono text-slate-700">
                            {editingField.fieldKey}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={openControlledReplyCreator}
                          disabled={!canManage || cardsLoading || Boolean(sectionErrors.cards)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add controlled reply
                        </button>
                      </div>

                      {controlledCardsForEditingField.length === 0 ? (
                        <p className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
                          {cardsLoading ? "Loading controlled replies..." : sectionErrors.cards ? "Controlled replies are unavailable. Retry the load." : "No controlled replies configured for this question yet."}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {controlledCardsForEditingField.map(card => (
                            <div
                              key={card.id}
                              className="rounded-lg border border-slate-200 bg-white p-3"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-slate-900">
                                      {card.title}
                                    </span>
                                    <span
                                      className={`rounded-full border px-2 py-0.5 text-xs ${
                                        card.isActive
                                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                          : "border-slate-200 bg-slate-100 text-slate-600"
                                      }`}
                                    >
                                      {card.isActive ? "Active" : "Inactive"}
                                    </span>
                                    <span
                                      className={`rounded-full border px-2 py-0.5 text-xs ${
                                        card.replyMode === "AiPolished"
                                          ? "border-amber-200 bg-amber-50 text-amber-700"
                                          : "border-blue-200 bg-blue-50 text-blue-700"
                                      }`}
                                    >
                                      {card.replyMode === "AiPolished"
                                        ? "Shadow only for live rollout"
                                        : "Live eligible if rollout allows"}
                                    </span>
                                  </div>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {(card.scenarioTags || []).map(tag => (
                                      <span
                                        key={tag}
                                        className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                  <p className="mt-2 text-sm text-slate-600">
                                    {shortPreview(card.approvedAnswer) || "No approved answer"}
                                  </p>
                                  <div className="mt-1 text-xs text-slate-500">
                                    Reply mode: {card.replyMode} - Priority: {card.priority ?? 0}
                                  </div>
                                </div>
                                <div className="flex flex-none flex-wrap gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => openControlledReplyEditor(card)}
                                    disabled={!canManage}
                                    className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setControlledReplyActive(card, !card.isActive)
                                    }
                                    disabled={!canManage}
                                    className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {card.isActive ? "Deactivate" : "Activate"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {editingControlledReply ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold text-slate-900">
                              {controlledReplyForm.id
                                ? "Edit controlled reply"
                                : "Add controlled reply"}
                            </h3>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingControlledReply(null);
                                setControlledReplyForm(EMPTY_CONTROLLED_REPLY_FORM);
                              }}
                              className="text-xs text-slate-500 hover:text-slate-800"
                            >
                              Cancel
                            </button>
                          </div>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <TextInput
                              label="Title"
                              name="title"
                              value={controlledReplyForm.title}
                              onChange={onControlledReplyChange}
                            />
                            <SelectInput
                              label="Reply mode"
                              name="replyMode"
                              value={controlledReplyForm.replyMode}
                              onChange={onControlledReplyChange}
                              options={CONTROLLED_REPLY_MODES}
                            />
                            <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                              Live mode currently sends only Configured controlled replies.
                              AiPolished replies are saved for shadow/evaluation and are not sent live yet.
                            </div>
                            <div className="md:col-span-2">
                              <div className="mb-1 text-sm text-slate-700">
                                Scenario tags
                              </div>
                              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                {CONTROLLED_REPLY_SCENARIOS.map(scenario => (
                                  <label
                                    key={scenario}
                                    className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                                  >
                                    <input
                                      type="checkbox"
                                      aria-label={scenario}
                                      checked={controlledReplyForm.scenarioTags.includes(
                                        scenario
                                      )}
                                      onChange={() =>
                                        toggleControlledReplyScenario(scenario)
                                      }
                                      className="mt-1"
                                    />
                                    <span>
                                      <span className="block font-medium text-slate-800">
                                        {scenario}
                                      </span>
                                      <span className="mt-0.5 block text-xs text-slate-500">
                                        {CONTROLLED_REPLY_SCENARIO_HELP[scenario]}
                                      </span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <TextInput
                              label="Approved answer"
                              name="approvedAnswer"
                              value={controlledReplyForm.approvedAnswer}
                              onChange={onControlledReplyChange}
                              multiline
                              rows={4}
                            />
                            <TextInput
                              label="Must not say"
                              name="mustNotSayText"
                              value={controlledReplyForm.mustNotSayText}
                              onChange={onControlledReplyChange}
                              multiline
                              rows={4}
                            />
                            <label className="block text-sm text-slate-700">
                              Priority
                              <input
                                type="number"
                                value={controlledReplyForm.priority}
                                onChange={event =>
                                  onControlledReplyChange(
                                    "priority",
                                    Number(event.target.value)
                                  )
                                }
                                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                              />
                            </label>
                            <label className="flex items-center gap-2 pt-7 text-sm text-slate-700">
                              <input
                                type="checkbox"
                                checked={controlledReplyForm.isActive}
                                onChange={event =>
                                  onControlledReplyChange(
                                    "isActive",
                                    event.target.checked
                                  )
                                }
                              />
                              Active
                            </label>
                          </div>

                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={saveControlledReply}
                              disabled={savingControlledReply || !canManage}
                              className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Save className="h-4 w-4" />
                              {savingControlledReply
                                ? "Saving..."
                                : "Save controlled reply"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveField}
              disabled={savingField}
              className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {savingField ? "Saving..." : editingField?.id ? "Save question" : "Create question"}
            </button>
          </div>
        </Modal>
      ) : null}

      {editingReminderStep ? (
        <Modal
          title={reminderStepForm.id ? "Edit reminder step" : "Add reminder step"}
          error={sectionErrors.mediaDetail} onRetry={() => loadReminderMediaAssetDetail(reminderStepForm.optionalMediaAssetId)}
          onClose={() => {
            setEditingReminderStep(null);
            setReminderStepForm(EMPTY_REMINDER_STEP_FORM);
            setSelectedReminderMediaAsset(null);
          }}
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Send text or approved media while the customer's WhatsApp reply window is open.
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block text-sm text-slate-700">
                Sequence
                <input
                  type="number"
                  min="1"
                  value={reminderStepForm.sequence}
                  onChange={event =>
                    onReminderStepChange("sequence", event.target.value === "" ? "" : Number(event.target.value))
                  }
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                />
              </label>
              <label className="block text-sm text-slate-700">
                Delay minutes
                <input
                  type="number"
                  min="1"
                  max="10080"
                  value={reminderStepForm.delayMinutes}
                  onChange={event =>
                    onReminderStepChange("delayMinutes", event.target.value === "" ? "" : Number(event.target.value))
                  }
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                />
              </label>
              <TextInput
                label="Message text"
                name="messageText"
                value={reminderStepForm.messageText}
                onChange={onReminderStepChange}
                multiline
                rows={5}
              />
              <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="block text-sm text-slate-700">
                    Optional media asset
                    <select
                      value={reminderStepForm.optionalMediaAssetId || ""}
                      onChange={event =>
                        onReminderStepChange(
                          "optionalMediaAssetId",
                          event.target.value || null
                        )
                      }
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      disabled={reminderMediaAssetsLoading}
                    >
                      <option value="">
                        {reminderMediaAssetsLoading ? "Loading approved media..." : "No media"}
                      </option>
                      {reminderMediaAssets.map(asset => {
                        const mediaType = normalizeReminderMediaType(asset?.mediaType);
                        const assetLabel = asset?.title || asset?.fileName || asset?.id;
                        const phoneHint = asset?.phoneNumberId ? ` - ${asset.phoneNumberId}` : "";
                        const expiryHint = asset?.expiresAtUtc
                          ? ` - expires ${new Date(asset.expiresAtUtc).toLocaleDateString()}`
                          : "";
                        return (
                          <option key={asset.id} value={asset.id}>
                            {mediaType ? `${mediaType}: ` : ""}
                            {assetLabel}
                            {phoneHint}
                            {expiryHint}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <TextInput
                    label="Optional media caption"
                    name="optionalMediaCaption"
                    value={reminderStepForm.optionalMediaCaption}
                    onChange={onReminderStepChange}
                    multiline
                    rows={4}
                    maxLength={REMINDER_MEDIA_CAPTION_MAX_LENGTH}
                    disabled={(!reminderStepForm.optionalMediaAssetId || normalizeReminderMediaType(reminderMediaAssetLookup.get(reminderStepForm.optionalMediaAssetId)?.mediaType) === "Audio") && !reminderStepForm.optionalMediaCaption}
                  />
                </div>
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  <p>
                    Only approved auto-send media assets with WhatsApp media IDs are eligible. Eligibility is checked again before sending.
                    Audio captions are unavailable; an older caption must be cleared.
                  </p>
                  <p>
                    If exact sender compatibility cannot be verified here, it will be checked again before sending.
                  </p>
                  {selectedReminderMediaAsset?.id ? (
                    <p>
                      Selected asset details loaded:{" "}
                      {selectedReminderMediaAsset.title ||
                        selectedReminderMediaAsset.fileName ||
                        selectedReminderMediaAsset.id}
                    </p>
                  ) : null}
                  {reminderMediaAssetsError ? (
                    <p className="text-amber-700">{reminderMediaAssetsError}</p>
                  ) : null}
                </div>
              </div>
              <div className="space-y-3 pt-6">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(reminderStepForm.includePendingQuestion)}
                    onChange={event =>
                      onReminderStepChange("includePendingQuestion", event.target.checked)
                    }
                  />
                  Include pending question
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(reminderStepForm.isActive)}
                    onChange={event =>
                      onReminderStepChange("isActive", event.target.checked)
                    }
                  />
                  Active
                </label>
                <label className="block text-sm text-slate-700">
                  <input type="checkbox" checked={Boolean(reminderStepForm.useTemplateOutsideWindow)} onChange={event => onReminderStepChange("useTemplateOutsideWindow", event.target.checked)} disabled={!reminderStepForm.useTemplateOutsideWindow} /> Outside-window template delivery (unavailable)
                  <span className="mt-1 block text-xs text-slate-500">This worker sends only inside the current WhatsApp window. Clear older template settings before saving.</span>
                </label>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
              Available placeholders: {" "}
              <code className="rounded bg-slate-100 px-1 py-0.5">{"{{customerName}}"}</code>{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5">{"{{pendingQuestion}}"}</code>{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5">{"{{businessName}}"}</code>{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5">{"{{lastAskedField}}"}</code>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveReminderStep}
              disabled={savingReminderStep || !canManage || reminderConfigLoading || !reminderSectionLoaded || Boolean(sectionErrors.reminders)}
              className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {savingReminderStep
                ? "Saving..."
                : reminderStepForm.id
                ? "Save reminder step"
                : "Create reminder step"}
            </button>
          </div>
        </Modal>
      ) : null}

      {editingRule ? (
        <Modal
          title={editingRule.id ? `Edit trigger rule: ${editingRule.triggerName}` : "Add media trigger rule"}
          onClose={() => setEditingRule(null)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Trigger name"
              name="triggerName"
              value={ruleForm.triggerName}
              onChange={onRuleChange}
            />
            <div className="space-y-2">
              <label className="block text-sm">WhatsApp sender (optional)
                <select value={ruleForm.phoneNumberId} onChange={event => onRuleChange("phoneNumberId", event.target.value)} className="mt-1 block w-full rounded border p-2">
                  <option value="">Any eligible sender</option>
                  {ruleForm.phoneNumberId && !senderOptions.some(row => row.phoneNumberId === ruleForm.phoneNumberId) ? <option value={ruleForm.phoneNumberId}>Saved sender (unavailable)</option> : null}
                  {senderOptions.map(row => <option key={row.phoneNumberId} value={row.phoneNumberId}>{senderLabel(row)}</option>)}
                </select>
              </label>
              <AiLoadError message={senderError} onRetry={() => setSenderRetry(value => value + 1)} label="senders" />
              <details><summary className="text-xs">Advanced sender identifier</summary><TextInput label="PhoneNumberId" name="phoneNumberId" value={ruleForm.phoneNumberId} onChange={onRuleChange} /></details>
            </div>
            <SelectInput
              label="Trigger type"
              name="triggerType"
              value={ruleForm.triggerType}
              onChange={onRuleChange}
              options={["IntentBased", "StepBased", "StateBased", "EventBased"]}
            />
            <SelectInput
              label="Trigger purpose"
              name="triggerPurpose"
              value={ruleForm.triggerPurpose}
              onChange={onRuleChange}
              options={["PrimaryAction", "SecondaryAction"]}
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Event type
                <span className="ml-1 text-xs font-normal text-slate-400">
                  (for event-based rules — optional)
                </span>
              </label>
              <select
                value={ruleForm.eventType}
                onChange={e => onRuleChange("eventType", e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">— Not set —</option>
                <option value="IntentConfirmed">IntentConfirmed</option>
                <option value="FieldCollected">FieldCollected</option>
                <option value="LocationCollected">LocationCollected</option>
                <option value="PhotoReceived">PhotoReceived</option>
                <option value="LeadCompleted">LeadCompleted</option>
                <option value="ExplicitMediaRequest">ExplicitMediaRequest</option>
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Existing TriggerType / Intent / Step behavior is unchanged.
              </p>
            </div>
            <SelectInput
              label="Repeat policy"
              name="repeatPolicy"
              value={ruleForm.repeatPolicy}
              onChange={onRuleChange}
              options={["OncePerSession", "EveryInbound"]}
            />
            <SelectInput
              label="Media category"
              name="mediaCategory"
              value={ruleForm.mediaCategory}
              onChange={onRuleChange}
              options={[
                "BeforeAfter",
                "Testimonial",
                "Brochure",
                "Catalog",
                "PriceList",
                "Menu",
                "ProductImage",
                "ServiceImage",
                "PropertyPhoto",
                "FloorPlan",
                "LocationMap",
                "CaseStudy",
                "Guide",
                "ReportSample",
                "Offer",
                "TeamIntro",
                "Other",
              ]}
            />
            <TextInput
              label="Intent"
              name="intent"
              value={ruleForm.intent}
              onChange={onRuleChange}
            />
            <TextInput
              label="Step"
              name="step"
              value={ruleForm.step}
              onChange={onRuleChange}
            />
            <TextInput
              label="Use case"
              name="useCase"
              value={ruleForm.useCase}
              onChange={onRuleChange}
            />
            <SelectInput
              label="Media type"
              name="mediaType"
              value={ruleForm.mediaType}
              onChange={onRuleChange}
              options={["Image", "Document", "Video"]}
              allowBlank
            />
            <label className="block text-sm text-slate-700">
              Max assets
              <input
                type="number"
                min={1}
                max={10}
                value={ruleForm.maxAssets}
                onChange={event =>
                  onRuleChange("maxAssets", Number(event.target.value))
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
              />
            </label>
            <label className="block text-sm text-slate-700">
              Priority
              <input
                type="number"
                value={ruleForm.priority}
                onChange={event =>
                  onRuleChange("priority", Number(event.target.value))
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
              />
            </label>
            <label className="block text-sm text-slate-700">
              Delay seconds
              <input
                type="number"
                min={0}
                value={ruleForm.delaySeconds}
                onChange={event =>
                  onRuleChange("delaySeconds", Number(event.target.value))
                }
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
              />
            </label>
            <div className="space-y-3 pt-6">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={ruleForm.requiresConsent}
                  onChange={event =>
                    onRuleChange("requiresConsent", event.target.checked)
                  }
                />
                Requires consent
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={ruleForm.isActive}
                  onChange={event =>
                    onRuleChange("isActive", event.target.checked)
                  }
                />
                Active
              </label>
            </div>
            <div className="md:col-span-2">
              <details className="rounded-lg border border-slate-200 p-3"><summary className="text-sm font-medium">Advanced rule conditions</summary>
              <TextInput
                label="Required fields JSON"
                name="requiredFieldsJson"
                value={ruleForm.requiredFieldsJson}
                onChange={onRuleChange}
                multiline
                rows={6}
              />
              </details>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveRule}
              disabled={savingRule}
              className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Settings2 className="h-4 w-4" />
              {savingRule ? "Saving..." : editingRule?.id ? "Save trigger rule" : "Create trigger rule"}
            </button>
          </div>
        </Modal>
      ) : null}

      {editingProof ? (
        <Modal
          title={editingProof.id ? `Edit location: ${editingProof.locationName}` : "Add proof location"}
          onClose={() => setEditingProof(null)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Location name"
              name="locationName"
              value={proofForm.locationName}
              onChange={onProofChange}
            />
            <TextInput
              label="District"
              name="district"
              value={proofForm.district}
              onChange={onProofChange}
            />
            <TextInput
              label="State"
              name="state"
              value={proofForm.state}
              onChange={onProofChange}
            />
            <TextInput
              label="Country"
              name="country"
              value={proofForm.country}
              onChange={onProofChange}
            />
            <div className="md:col-span-2">
              <details className="rounded-lg border border-slate-200 p-3"><summary className="text-sm font-medium">Advanced nearby-area data</summary>
              <TextInput
                label="Nearby areas JSON"
                name="nearbyAreasJson"
                value={proofForm.nearbyAreasJson}
                onChange={onProofChange}
                multiline
                rows={3}
              />
              </details>
            </div>
            <div className="md:col-span-2">
              <TextInput
                label="Approved wording template"
                name="approvedWordingTemplate"
                value={proofForm.approvedWordingTemplate}
                onChange={onProofChange}
                multiline
                rows={3}
              />
            </div>
            <div className="md:col-span-2">
              <TextInput
                label="Disclaimer"
                name="disclaimer"
                value={proofForm.disclaimer}
                onChange={onProofChange}
                multiline
                rows={2}
              />
            </div>
            <div className="md:col-span-2">
              <details className="rounded-lg border border-slate-200 p-3"><summary className="text-sm font-medium">Advanced alias data</summary>
              <TextInput
                label="Aliases JSON"
                name="aliasesJson"
                value={proofForm.aliasesJson}
                onChange={onProofChange}
                multiline
                rows={3}
              />
              </details>
            </div>
            <div className="pt-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={proofForm.isActive}
                  onChange={event => onProofChange("isActive", event.target.checked)}
                />
                Active
              </label>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={saveProof}
              disabled={savingProof}
              className="inline-flex items-center gap-2 rounded-md border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {savingProof ? "Saving..." : editingProof?.id ? "Save location" : "Create location"}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default withAiAgentScope(AiLeadQualificationConfigPage);
